/**
 * SitemapsTab.tsx  (chunk3z-fix387)
 *
 * Site-relaunch migration prep. Mirrors the Content Generation tab's
 * left-to-right step order so the team's muscle memory transfers:
 *
 *   1 Scan history   → Wayback CDX scan of every old URL (server-side)
 *   2 Current site   → import pages.xml / posts.xml / sitemap_index.xml
 *   3 Review         → old URL table (junk toggles, generation badges)
 *                      + current-site tree
 *
 * Redirect mapping and the missing-pages report are the next drop — they carry
 * the classification rules from the migration workflow doc.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  getMigration,
  setMigrationUrlOverride,
  buildRedirects,
  getRedirects,
  updateRedirect,
  deleteRedirect,
  getRedirectCsvUrl,
  scanOldDomain,
  getOldSources,
  getOldDomainRedirectsUrl,
  patchOldSource,
  getTargetOptions,
  type OldSourceMatch,
  type Client,
  getMissingPages,
  type MigrationRedirect,
  type RedirectVerify,
  type LocationAnalysis,
  runWaybackScan,
  importSitemap,
  crawlSitemap,
  recomputeGenerations,
  getGscStatus,
  getGscConnectUrl,
  getGscProperties,
  disconnectGsc,
  importGsc,
  setMigrationUrlJunk,
  type MigrationRecord,
  type MigrationUrl,
  type MigrationTarget,
  type MigrationCounts,
} from "../lib/api";
import { Button, Field, TextInput } from "../components/Form";
import { useToast } from "../components/Toast";
import type { WorkspaceContext } from "./ClientWorkspacePage";

/** chunk3z-fix389/517: build the standard Yoast/RankMath sitemap index URL
 *  (/sitemap_index.xml) from a domain. No longer auto-prefilled — used by the
 *  "Load Yoast Sitemap" link so the operator opts in, since not every incoming
 *  client runs Yoast and a wrong guessed URL previously looked authoritative. */
function guessSitemapUrl(domainOrUrl: string): string {
  const host = domainOrUrl.trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
  return host ? `https://${host}/sitemap_index.xml` : "";
}

type StepKey = "setup" | "review" | "redirects";

/**
 * chunk3z-fix453: categorize a current-site page for the target dropdown, so the
 * long flat list is grouped into SERVICES / BLOG POSTS / OTHER. The signal is
 * the sitemap child it came from (Yoast "post" → blog) plus path shape. Kept
 * deliberately simple and path-based so it works across platforms where
 * sitemapType is null (crawled sites, non-WordPress).
 */
type TargetCategory = "services" | "blog" | "other";

function categorizeTarget(path: string, sitemapType: string | null): TargetCategory {
  const p = path.toLowerCase();
  const t = (sitemapType ?? "").toLowerCase();

  // Blog: Yoast/RankMath "post" sitemap, or a /blog//news//articles path.
  if (t === "post" || t === "posts") return "blog";
  if (/^\/(blog|news|articles?|posts?)(\/|$)/.test(p)) return "blog";

  // Services: a /services/ path, or a "page" under a services-like segment.
  if (/(^|\/)(services?|treatments?|procedures?)(\/|$)/.test(p)) return "services";

  return "other";
}

const CATEGORY_LABELS: Record<TargetCategory, string> = {
  services: "SERVICES",
  blog: "BLOG POSTS",
  other: "OTHER",
};
const CATEGORY_ORDER: TargetCategory[] = ["services", "other", "blog"];

/*
 * chunk3z-fix629: richer, fixed-order grouping for the CROSS-DOMAIN target
 * dropdown (the "Moved from a different domain?" match table). Eddie wants the
 * options laid out in the site order he already knows, so picking a target is
 * faster:
 *   MAIN (fixed order) → BLOG POSTS (Blog Home + indented posts) → OTHER → UNKNOWN
 *
 * Path-based only — the backend target-options give us { url, path } with no
 * sitemapType, so every signal here comes from the slug. Each MAIN slot is
 * keyed by keyword; the FIRST target whose path matches a slot's test lands in
 * that slot, and MAIN slots render in the fixed order below regardless of how
 * many targets match. Anything we can't confidently place falls to UNKNOWN,
 * which stays visible as the catch-all Eddie scans.
 *
 * Blog detection at the ROOT (posts with no /blog/ prefix, as on SMC-generated
 * sites) uses a conservative question/editorial-phrase heuristic — a loose
 * "any long hyphenated slug = post" rule would wrongly bury real service/
 * location pages (e.g. /comprehensive-dental-checkups-.../) under BLOG POSTS.
 * Misplaced-but-findable (UNKNOWN) beats hidden-where-you-won't-look.
 */
type XDomGroupKey = "main" | "blog" | "other" | "sitePages" | "unknown";

const XDOM_GROUP_LABELS: Record<XDomGroupKey, string> = {
  main: "MAIN",
  blog: "BLOG POSTS",
  other: "OTHER",
  // fix655: site-wide legal/policy pages shared across all locations.
  sitePages: "SITE PAGES",
  unknown: "UNKNOWN",
};
const XDOM_GROUP_ORDER: XDomGroupKey[] = ["main", "blog", "other", "sitePages", "unknown"];

// Fixed MAIN slot order + the label shown as an indented hint. `test` runs on a
// lowercased, trailing-slash-trimmed path.
type XDomMainSlot = { key: string; label: string; test: (p: string) => boolean };
const XDOM_MAIN_SLOTS: XDomMainSlot[] = [
  { key: "home", label: "Home", test: (p) => p === "" || p === "/" },
  {
    key: "about",
    label: "About Us / About Our Office",
    test: (p) => /(^|\/)(about|about-us|about-our-office|our-office|our-practice)(\/|$)/.test(p),
  },
  {
    key: "team",
    label: "Meet the Doctor / Meet the Team",
    test: (p) =>
      /(^|\/)(meet-the-|meet-our-|our-team|our-doctors?|the-team|our-dentists?|our-staff|dr-)/.test(
        p
      ) || /(^|\/)(doctors?|dentists?|team|staff)(\/|$)/.test(p),
  },
  {
    key: "services",
    label: "Services",
    test: (p) => /(^|\/)(services?|treatments?|procedures?)(\/|$)/.test(p),
  },
  {
    key: "resources",
    label: "Patient Resources",
    // fix632-followup: also catch first-visit / what-to-expect, financial +
    // payment variants, and insurance — all belong under MAIN Patient Resources.
    // Trailing (\/|-|$) so hyphen-compound slugs match too: patient-forms,
    // financial-information, insurance-and-financing, payment-options, etc.
    test: (p) =>
      /(^|\/)(patient|patients|new-patient|new-patients|patient-resources?|patient-info|resources?|forms?|first-visit|first-time|what-to-expect|financial|financials|financing|finance|payment|payments|insurance|insurances)(\/|-|$)/.test(
        p
      ),
  },
  {
    key: "offers",
    label: "Offers / Specials",
    test: (p) =>
      /(^|\/)(offers?|specials?|special-offers?|promotions?|promo|deals?|new-patient-special)(\/|$)/.test(
        p
      ),
  },
  {
    key: "reviews",
    label: "Reviews / Testimonials",
    test: (p) => /(^|\/)(reviews?|testimonials?)(\/|$)/.test(p),
  },
  {
    key: "contact",
    label: "Contact",
    test: (p) => /(^|\/)(contact|contact-us|book|appointments?|schedule|location|directions)(\/|$)/.test(p),
  },
];

// OTHER = legal / policy / accessibility boilerplate.
function xdomIsOther(p: string): boolean {
  return /(^|\/)(privacy|privacy-policy|website-accessibility|accessibility|accessibility-policy|hipaa|terms|terms-of-use|terms-of-service|disclaimer|disclaimers|policy|policies|sitemap|cookie|cookies)(\/|-|$)/.test(
    p
  );
}

// A root-level path (no /blog/ prefix) that reads like an editorial post.
//
// fix629 (revised): distinguishes a long descriptive POST slug
// (how-to-care-for-my-dental-implants) from a bare SERVICE slug
// (dental-implants). Signals, in priority order:
//
//   1. Service/location markers ALWAYS win → page. If the slug carries a
//      location suffix (-in-<city>, -at-<practice>, a US state code) or the
//      words "dentistry"/"aesthetic", it's one of the generated service/
//      location pages, never a post — even if it also says "guide-to". This is
//      Eddie's explicit call: burying a service page under BLOG POSTS is worse
//      than missing the rare true post that names a city.
//   2. Short slug (≤3 words) → page. Bare services (dental-implants,
//      teeth-whitening) and short marketing pages (how-it-works, why-choose-us)
//      stay out of BLOG POSTS.
//   3. Question opener / editorial vocab → post.
//   4. Long slug (≥5 words) with no service marker → post (the catch-all that
//      gets how-to-care-for-my-dental-implants without needing a keyword list).
function xdomLooksLikeRootPost(p: string): boolean {
  const slug = p.replace(/^\//, "").replace(/\/+$/, "");
  if (!slug || slug.includes("/")) return false; // only single-segment root slugs

  const words = slug.split("-").filter(Boolean).length;

  // 1. Service/location page markers — these WIN unconditionally.
  const looksLikeServicePage =
    /(-in-[a-z]+|-at-[a-z]+|-(pa|ga|nc|ca|ny|tx|fl|az|nj|va|md|oh|il|mi|wa|or|co)(-|$)|dentistry|aesthetic)/.test(
      slug
    );
  if (looksLikeServicePage) return false;

  // 2. Short slug = a page (service name or marketing page), never a post.
  if (words <= 3) return false;

  // 3. Question / editorial phrasing.
  const questionOpener =
    /^(can|are|is|do|does|should|will|when|what|why|how|which|who)-/.test(slug);
  const editorialVocab =
    /(debunking|myths?|myth|tips?|reasons?|ways?|benefits-of|signs-of|what-to-expect|understanding|everything-you|holiday|thanksgiving|christmas|new-year|gratitude|for-no-reason|separating-fact|how-to|guide-to)/.test(
      slug
    );
  if (questionOpener || editorialVocab) return true;

  // 4. Long descriptive slug with no service marker → post.
  return words >= 5;
}

// fix649: derive a location path prefix from the client's website URL (e.g.
// https://dagsmiles.com/northeast-philadelphia/ → "/northeast-philadelphia").
// For a multi-location client, the location's HOME PAGE is the bare location
// path itself, and its other pages hang off it (/northeast-philadelphia/about/,
// …). Stripping this prefix before categorizing means location pages slot into
// MAIN/Home/About/Services just like a single-location site's root pages —
// instead of the bare location path falling into UNKNOWN.
function xdomLocationPrefix(websiteUrl: string | null | undefined): string | null {
  if (!websiteUrl) return null;
  let raw = websiteUrl.trim();
  if (!raw) return null;
  try {
    if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;
    let p = new URL(raw).pathname.toLowerCase().replace(/\/+$/, "");
    if (!p) return null;
    if (!p.startsWith("/")) p = "/" + p;
    if (/^\/(index\.php|home|home-page)$/.test(p)) return null;
    return p;
  } catch {
    return null;
  }
}

// Strip the location prefix from a path so it categorizes relative to the
// location root. "/northeast-philadelphia" → "" (Home); "/northeast-philadelphia/about" → "/about".
function stripLocationPrefix(path: string, prefix: string | null): string {
  if (!prefix) return path;
  const p = path.replace(/\/+$/, "");
  const pre = prefix.replace(/\/+$/, "");
  if (p === pre) return ""; // the bare location path = the location home page
  if (p.toLowerCase().startsWith(pre.toLowerCase() + "/")) {
    return p.slice(pre.length); // keep the leading slash of the remainder
  }
  return path; // not under this location — leave as-is
}

function xdomCategorize(
  path: string,
  locationPrefix?: string | null
): { group: XDomGroupKey; mainKey?: string } {
  const p = stripLocationPrefix(path, locationPrefix ?? null)
    .toLowerCase()
    .replace(/\/+$/, "");

  // Blog home + anything under it (/blog/, /blogs/, /posts/, /news/, /articles/).
  if (/^\/(blogs?|news|articles?|posts?)(\/|$)/.test(p)) {
    return { group: "blog" };
  }
  // MAIN slots, first match wins, in fixed order.
  for (const slot of XDOM_MAIN_SLOTS) {
    if (slot.test(p)) return { group: "main", mainKey: slot.key };
  }
  // fix655: site-wide legal/policy pages (privacy, accessibility, hipaa, terms,
  // disclaimer, sitemap) — shared across all locations, grouped separately.
  if (
    /(^|\/)(privacy(-policy)?|accessibility(-statement)?|hipaa(-notice)?|terms(-of-service|-of-use|-and-conditions)?|disclaimer|sitemap)(\/|$)/.test(
      p
    )
  ) {
    return { group: "sitePages" };
  }
  // Legal / policy.
  if (xdomIsOther(p)) return { group: "other" };
  // Root-level editorial post.
  if (xdomLooksLikeRootPost(p)) return { group: "blog" };
  return { group: "unknown" };
}

type XDomTargetOption = { url: string; path: string };

// Build the grouped, ordered option structure for the cross-domain dropdown.
function buildXDomGroups(
  targets: XDomTargetOption[],
  locationPrefix?: string | null
) {
  // fix632-followup: MAIN slots are now multi-page. Each slot collects ALL its
  // matching targets — e.g. Services holds /services/ plus every
  // /services/<child>/, and Patient Resources holds the hub plus forms,
  // financial, insurance, new-patients, first-visit, etc. The hub (shortest /
  // exact path) leads the slot; the rest render indented beneath it, like the
  // blog posts. (Was single-occupancy: only the first match landed in MAIN and
  // extras fell to UNKNOWN.)
  const mainBySlot = new Map<string, XDomTargetOption[]>();
  const blogHome: XDomTargetOption[] = [];
  const blogPosts: XDomTargetOption[] = [];
  const other: XDomTargetOption[] = [];
  const sitePages: XDomTargetOption[] = [];
  const unknown: XDomTargetOption[] = [];

  for (const t of targets) {
    const { group, mainKey } = xdomCategorize(t.path, locationPrefix ?? null);
    if (group === "main" && mainKey) {
      const arr = mainBySlot.get(mainKey) ?? [];
      arr.push(t);
      mainBySlot.set(mainKey, arr);
    } else if (group === "blog") {
      // Use the location-relative path so /northeast-philadelphia/blog/ is the
      // blog HOME, and /northeast-philadelphia/blog/<post> is a post.
      const p = stripLocationPrefix(t.path, locationPrefix ?? null)
        .toLowerCase()
        .replace(/\/+$/, "");
      // A bare /blog//blogs//news//articles//posts is the blog landing page;
      // anything deeper (or a root-level editorial slug) is an individual post.
      if (/^\/(blogs?|news|articles?|posts?)$/.test(p)) {
        blogHome.push(t);
      } else {
        blogPosts.push(t);
      }
    } else if (group === "other") {
      other.push(t);
    } else if (group === "sitePages") {
      sitePages.push(t);
    } else {
      unknown.push(t);
    }
  }

  // Assemble MAIN in the fixed slot order. Within a slot, the hub (fewest path
  // segments, then shortest) leads; children follow alphabetically and render
  // indented.
  const main: { slot: XDomMainSlot; hub: XDomTargetOption | null; children: XDomTargetOption[] }[] =
    [];
  for (const slot of XDOM_MAIN_SLOTS) {
    const opts = mainBySlot.get(slot.key);
    if (!opts || opts.length === 0) continue;
    const segCount = (o: XDomTargetOption) =>
      o.path.replace(/\/+$/, "").split("/").filter(Boolean).length;
    const sorted = [...opts].sort((a, b) => {
      const sa = segCount(a);
      const sb = segCount(b);
      if (sa !== sb) return sa - sb;
      return a.path.localeCompare(b.path);
    });
    main.push({ slot, hub: sorted[0] ?? null, children: sorted.slice(1) });
  }

  blogPosts.sort((a, b) => a.path.localeCompare(b.path));
  other.sort((a, b) => a.path.localeCompare(b.path));
  sitePages.sort((a, b) => a.path.localeCompare(b.path));
  unknown.sort((a, b) => a.path.localeCompare(b.path));

  return { main, blogHome, blogPosts, other, sitePages, unknown };
}


// chunk3z-fix388: the archive scan and the current-site sitemap are one setup
// step — you need both before the generations mean anything.
const STEPS: { key: StepKey; n: number; label: string }[] = [
  { key: "setup", n: 1, label: "Setup" },
  { key: "review", n: 2, label: "Review" },
  { key: "redirects", n: 3, label: "Redirects & Report" },
];

/**
 * chunk3z-fix435: classify a GSC property string for display. Domain properties
 * arrive as "sc-domain:example.com"; URL-prefix ones as "https://example.com/".
 * Showing the type is the "what is what" the raw list didn't make obvious.
 */
function gscPropMeta(pr: string): {
  label: string;
  badge: string;
  cls: string;
  title: string;
} {
  if (pr.startsWith("sc-domain:")) {
    return {
      label: pr.replace(/^sc-domain:/, ""),
      badge: "Domain",
      cls: "bg-outline-bg text-outline",
      title:
        "Domain property — covers every subdomain and both http/https for this domain",
    };
  }
  return {
    label: pr.replace(/\/+$/, ""),
    badge: "URL-prefix",
    cls: "bg-accent-subtle text-accent",
    title:
      "URL-prefix property — covers only URLs under this exact protocol and path",
  };
}

// chunk3z-fix493: the scan summary must persist per-client on the Review tab —
// it survives tab switches and returns when you come back to this client, but
// hides once the user ✕'s it or switches to another client. A module-level map
// (client id → summary lines) holds it across component mounts within a session.
const scanSummaryCache = new Map<number, string[]>();

export default function SitemapsTab() {
  const { client } = useOutletContext<WorkspaceContext>();
  const toast = useToast();

  // The scan summary shown as an inline banner on Review, scoped to this client.
  const [scanSummary, setScanSummary] = useState<string[] | null>(
    scanSummaryCache.get(client.id) ?? null
  );

  const [step, setStep] = useState<StepKey>("setup");
  // chunk3z-fix447: the current-site tree is collapsed by default on Review —
  // it's a lot of vertical space that's rarely needed once the page count is
  // trusted. A subtle toggle by the Next button reveals it on demand.
  const [showStructure, setShowStructure] = useState(false);
  // chunk3z-fix477: show the found pages as a parent/child tree. Defaults on for
  // a not-launched prospect (their found pages ARE the site structure).
  const [pagesAsTree, setPagesAsTree] = useState(false);
  // chunk3z-fix488: URLs the user manually moved to Blog posts (overrides the
  // auto page/post heuristic). Kept in local state for the session.
  const [forcedBlog, setForcedBlog] = useState<Set<number>>(new Set());
  // chunk3z-fix491: URLs the user manually moved under the services hub.
  const [forcedService, setForcedService] = useState<Set<number>>(new Set());
  // chunk3z-fix509: URLs the user manually moved OUT of Blog posts into Pages.
  // Distinct from forcedService (which reparents under the services hub): a page
  // like /why-choose-us is a top-level "other" page, not a service, so it should
  // sit in Pages at the root, not nested under /services/. Overrides both the
  // looksLikeBlogPost heuristic and a post-type sitemap classification.
  const [forcedPage, setForcedPage] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);

  const [migration, setMigration] = useState<MigrationRecord | null>(null);
  const [urls, setUrls] = useState<MigrationUrl[]>([]);
  const [targets, setTargets] = useState<MigrationTarget[]>([]);
  const [counts, setCounts] = useState<MigrationCounts | null>(null);

  const [domain, setDomain] = useState("");
  const [launchDate, setLaunchDate] = useState("");
  // chunk3z-fix418: true once the user has touched the field this session. Lets
  // us tell "not loaded yet" (adopt the saved date) from "deliberately cleared"
  // (leave it empty) — without it, clearing the field silently refilled from the
  // database and the date couldn't be blanked.
  const [launchDateTouched, setLaunchDateTouched] = useState(false);
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [sitemapXml, setSitemapXml] = useState("");
  // chunk3z-fix388: the client hasn't launched with us yet — everything found
  // is their current live site. Becomes Gen 2 automatically once a launch date
  // is entered, because generation is derived from dates, not stored.
  const [notLaunched, setNotLaunched] = useState(false);
  const [prevRelaunch, setPrevRelaunch] = useState("");
  const [recomputing, setRecomputing] = useState(false);
  // chunk3z-fix395: redirect map + missing-pages report
  const [redirects, setRedirects] = useState<MigrationRedirect[]>([]);
  const [verify, setVerify] = useState<RedirectVerify | null>(null);
  const [building, setBuilding] = useState(false);
  // fix685: elapsed seconds while the redirect map is building, so a first-time
  // visitor sees an obvious "working…" signal with a running timer rather than
  // just a greyed-out button.
  const [buildElapsed, setBuildElapsed] = useState(0);
  const [report, setReport] = useState<string>("");
  const [reportCounts, setReportCounts] = useState<{
    servicePages: number; blogPosts: number; other: number; notHundred: number;
  } | null>(null);
  const [redirFilter, setRedirFilter] = useState<
    "all" | "review" | "unmatched"
  >("all");
  // chunk3z-fix402: multi-location rule — generic old pages need an explicit
  // location choice before they can be matched.
  const [locations, setLocations] = useState<LocationAnalysis | null>(null);
  const [defaultLocation, setDefaultLocation] = useState<string>("");
  // chunk3z-fix408: sitemap errors are detailed and actionable, so they persist
  // in the panel rather than only flashing past in a toast.
  const [sitemapError, setSitemapError] = useState<string | null>(null);
  // chunk3z-fix424: Search Console cross-check.
  const [gsc, setGsc] = useState<{ connected: boolean; configured: boolean }>({
    connected: false,
    configured: false,
  });
  const [gscProps, setGscProps] = useState<string[]>([]);
  // chunk3z-fix428: a client often has BOTH a domain property and a URL-prefix
  // property, and which one holds the real history is unpredictable — it
  // depends on which was verified first, not on the property type. On one real
  // client the URL-prefix property had 1.29K clicks over 16 months while the
  // domain property had 64. Pulling either alone loses data, so this is a
  // multi-select and results are merged.
  const [gscSites, setGscSites] = useState<string[]>([]);
  const [gscBusy, setGscBusy] = useState(false);
  const [gscError, setGscError] = useState<string | null>(null);
  const [gscLoadingProps, setGscLoadingProps] = useState(false);
  // chunk3z-fix431: with hundreds of properties on the agency account, showing
  // every one makes finding this client's tedious. Default to the ones whose
  // host matches the client domain, with an escape hatch to see everything —
  // a property is occasionally verified under a different domain (an old name,
  // or a .net alongside a .com).
  const [gscShowAll, setGscShowAll] = useState(false);

  // Table filters
  const [showJunk, setShowJunk] = useState(false);
  // chunk3z-fix405: column sorting for the Old URLs table.
  const [sortKey, setSortKey] = useState<
    "path" | "firstCapture" | "lastCapture" | "generation" | "source"
  >("path");
  const [sortAsc, setSortAsc] = useState(true);
  // chunk3z-fix401: this table exists to review pages that NEED work — the ones
  // that stopped existing. Gen 1 pages survived the rebuild (often at the same
  // URL), so they need no redirect and are not gaps. There is no filter UI:
  // the scope is derived from launch state. For a PRE-LAUNCH client every page
  // is still live, so "Gen 2 and older" would be empty — show everything then.
  const [search] = useState("");

  async function load() {
    setLoading(true);
    try {
      const data = await getMigration(client.id);
      setMigration(data.migration);
      setUrls(data.urls);
      setTargets(data.targets);
      setCounts(data.counts);
      // chunk3z-fix510: rehydrate the manual page/blog/service moves from the
      // persisted overrides (keyed by path) into the session sets (keyed by url
      // id). A page can appear in the tree as either a found URL or a target, so
      // map every id that shares an overridden path. Rebuilt from scratch each
      // load so a cleared override doesn't linger.
      {
        const byCat: Record<string, Set<number>> = {
          page: new Set(),
          blog: new Set(),
          service: new Set(),
        };
        const ov = data.overrides ?? {};
        for (const u of data.urls) {
          const cat = ov[u.path];
          if (cat && byCat[cat]) byCat[cat]!.add(u.id);
        }
        setForcedPage(byCat.page!);
        setForcedBlog(byCat.blog!);
        setForcedService(byCat.service!);
      }
      if (data.migration) {
        setDomain(data.migration.domain);
        // chunk3z-fix412: only adopt the server's launch date when the field is
        // still empty. load() runs after Load Pages, and it was overwriting a
        // date the user had just typed but not yet saved — which also flipped
        // "Not launched yet" on, because the stored value was still null.
        setLaunchDate((cur) =>
          launchDateTouched ? cur : data.migration!.launchDate || cur || ""
        );
        setPrevRelaunch(data.migration.prevRelaunchDate ?? "");
        // fix620: show the persisted resolved sitemap URL in the field on every
        // visit once a sitemap has been loaded (not just right after loading).
        if (data.migration.sitemapUrl) {
          setSitemapUrl((cur) => cur || data.migration!.sitemapUrl || "");
        }
        setNotLaunched((cur) => {
          // chunk3z-fix504: trust the PERSISTED intent first. A launch date means
          // launched. An explicit stored not_launched=true means prospect — this
          // survives navigating away and back (the state used to reset to false
          // on remount, scrambling the Review view). Fall back to current state
          // only when the server has no opinion yet.
          if (data.migration!.launchDate) return false;
          if (data.migration!.notLaunched) return true;
          return cur;
        });
        // chunk3z-fix477/504: for a not-launched client the found pages ARE
        // their site — default to the structure (tree) view. Use the persisted
        // intent so this holds after remount.
        if (!data.migration.launchDate && data.migration.notLaunched !== false)
          setPagesAsTree(true);
        // fix687: DON'T auto-jump to Redirects & Report on return. fix634 used
        // to land here directly when a map existed, but that made the tab "hop"
        // to Redirects every time the user came back from another section, which
        // is disorienting. We still preload the redirects + report below so
        // they're ready the instant the user clicks that step — we just don't
        // move them off Setup automatically. (The mount effect already resets to
        // "setup"; we simply no longer advance past it.)
        void loadRedirects();
        void loadReport();
      } else {
        // Prefill the domain from the client record — a teammate should only
        // need to type a launch date.
        setDomain(client.websiteUrl);
      }
      // chunk3z-fix517: DON'T auto-prefill the sitemap URL. Not every incoming
      // client runs Yoast/RankMath, so a guessed /sitemap_index.xml was often a
      // wrong URL that looked authoritative. The field now starts blank; a
      // "Load Yoast Sitemap" link beside it fills the guess on demand for the
      // common case, without pretending it's right when it isn't.
      return data.counts;
    } catch {
      /* first load with no migration is not an error */
      return null;
    } finally {
      setLoading(false);
    }
    return null;
  }

  useEffect(() => {
    // chunk3z-fix411: always open on Setup and pull fresh data. Navigating
    // between workspace tabs unmounts this component, so this runs on every
    // visit; resetting explicitly also clears anything left over when switching
    // clients (redirect map, report, per-client errors).
    setStep("setup");
    setLaunchDateTouched(false);
    setRedirects([]);
    setVerify(null);
    setReport("");
    setReportCounts(null);
    setLocations(null);
    setDefaultLocation("");
    setSitemapError(null);
    void load();
    void getGscStatus()
      .then((st) => {
        setGsc(st);
        // chunk3z-fix428: fetch properties as soon as we know we're connected,
        // rather than waiting for the dropdown to be focused — that left the
        // control reading "Loading…" until clicked.
        if (st.connected) void handleGscRefresh();
      })
      .catch(() => setGsc({ connected: false, configured: false }));
    // chunk3z-fix493: reflect this client's cached scan summary (or none).
    setScanSummary(scanSummaryCache.get(client.id) ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  // fix685: tick the build elapsed-time counter once per second while the map
  // is building, for the progress banner.
  useEffect(() => {
    if (!building) return;
    const id = setInterval(() => setBuildElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [building]);

  // chunk3z-fix399: the redirect map is derived entirely from data we already
  // have (wayback URLs + sitemap targets), so there's no reason to make someone
  // press a button for it. Build automatically on first visit to the step; the
  // Rebuild button stays for after junk/generation edits.
  useEffect(() => {
    if (step !== "redirects") return;
    if (building || redirects.length > 0) return;
    if (urls.length === 0 || targets.length === 0) return;
    void handleBuildRedirects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, urls.length, targets.length]);

  async function handleScan() {
    if (!domain.trim()) {
      toast.show("Enter a domain to scan", { kind: "error" });
      return;
    }
    setScanning(true);
    try {
      // chunk3z-fix478: for a NOT-LAUNCHED onboarding client, crawl their current
      // live site FIRST. The crawl defines "the current site" — the pages we're
      // responsible for transferring. Wayback/GSC then add redirect coverage for
      // older URLs, but those pre-current-site pages are redirect-only (hidden
      // from Pages found). Running the crawl here means one button does it all.
      let crawlCount = 0;
      // chunk3z-fix514: track HOW the current site was discovered and whether it
      // failed, so the summary can say so instead of silently falling through to
      // a thin homepage-only result. "sitemap" = read a real XML/HTML sitemap;
      // "spider" = no sitemap reachable, followed homepage links; "failed" = the
      // fetch threw (blocked/timeout) and we have nothing fresh.
      let crawlVia: "sitemap" | "spider" | "failed" | null = null;
      // chunk3z-fix504: for a not-launched client, (re)load the current site from
      // the full sitemap index so we get pages AND posts. A user who did "Load
      // Pages" against page-sitemap.xml only would otherwise miss the blog POSTS.
      // chunk3z-fix516: but do this WITHOUT destroying targets that were already
      // loaded. The old code always passed replace:true, so on a WAF-protected
      // client — where "Load Pages" (direct URL) succeeds but auto-discovery is
      // blocked — the scan would DELETE the loaded pages, then fail to re-fetch
      // them, leaving "1 current page". Now: if targets already exist, crawl with
      // replace:false so discovery only ADDS anything missing (e.g. posts) and a
      // blocked/empty discovery is a harmless no-op. Only replace when we have
      // nothing yet.
      const haveTargets = targets.length > 0;
      if (notLaunched) {
        try {
          const c = await crawlSitemap(client.id, {
            // Prefer the sitemap index so BOTH page- and post-sitemaps are read.
            url: sitemapUrl.trim() || domain.trim() || undefined,
            replace: !haveTargets,
            launchDate: null,
          });
          crawlCount = c.imported;
          // fix516: if discovery came back empty but we already had targets from
          // Load Pages, that's not a failure — the loaded set stands. Report the
          // method honestly: a WAF-blocked re-crawl shouldn't claim "no sitemap".
          crawlVia = c.imported === 0 && haveTargets ? "sitemap" : c.via;
        } catch (e) {
          // fix516: a thrown discovery is only a real failure when we have nothing
          // to stand on. If Load Pages already populated targets, keep them.
          crawlVia = haveTargets ? "sitemap" : "failed";
          // Only warn if we truly have nothing to fall back on.
          if (targets.length === 0) {
            toast.show(
              "Couldn't load the live site's sitemap. " +
                "Go to the Setup tab, paste the sitemap URL into \"Current site pages\", and click Load Pages, then scan again.",
              { kind: "info" }
            );
          }
        }
      }

      // 1) Wayback first — the archive history and its capture dates.
      const res = await runWaybackScan(client.id, {
        domain: domain.trim(),
        // chunk3z-fix418: send null rather than undefined for an empty date.
        // undefined is dropped from the JSON body entirely, so a deliberately
        // cleared date wouldn't reach the server to be cleared there.
        launchDate: notLaunched ? null : launchDate.trim() || null,
        notLaunched, // chunk3z-fix504: persist the prospect intent
        prevRelaunch: prevRelaunch.trim() || undefined,
      });
      if (res.prevRelaunch) setPrevRelaunch(res.prevRelaunch);

      // 2) chunk3z-fix455: then pull GSC in the SAME action (if connected and
      // properties are selected), so the whole pipeline runs from one button in
      // the right order — Wayback dates land first, so GSC only needs to date the
      // pages it uniquely found. The standalone "GSC only" link remains for when
      // Search Console is connected after a scan.
      // chunk3z-fix474: build a VERTICAL, labeled summary. The toast renders
      // with whitespace-pre-line, so newlines stack. A horizontal one-liner made
      // the numbers hard to tell apart.
      let gscAdded = 0;
      let gscUpdated = 0;
      let gscSitemapAdded = 0;
      let gscRan = false;
      if (gsc.connected && gscVisibleSelected.length > 0) {
        const g = await runGscImport();
        if (g.ran) {
          gscRan = true;
          gscAdded = g.added;
          gscUpdated = g.updated;
          gscSitemapAdded = g.sitemapAdded;
          if (g.failures.length > 0) setGscError(g.failures.join(" · "));
        }
      }

      // chunk3z-fix496: build the summary from the FINAL counts (after load()),
      // not the mid-scan Wayback response. The old version reported res.kept/junk
      // from Wayback alone, so it disagreed with the counts strip once GSC pages
      // and image-sitemap filtering were applied during load — confusing.
      const finalCounts = await load();
      const kept = finalCounts?.kept ?? res.kept;
      const junk = finalCounts?.junk ?? res.junk;
      const lines = [
        "Scan complete",
        `${kept} usable pages found`,
        `${junk} filtered as junk`,
      ];
      if (notLaunched && crawlVia) {
        // chunk3z-fix514/515: state HOW the current site was loaded as the very
        // first status line, so "was a sitemap found?" is answered at a glance.
        // A homepage-only spider result is the classic symptom of a blocked
        // sitemap fetch — call it out explicitly with a warning marker.
        // chunk3z-fix516: report the TOTAL current-site page count, not just the
        // number this crawl added. When targets were pre-loaded via Load Pages
        // and the scan's re-crawl added 0, crawlCount is 0 but the real count is
        // whatever Load Pages brought in — show that.
        const currentPageCount = finalCounts?.targets ?? crawlCount;
        if (crawlVia === "sitemap") {
          lines.splice(1, 0, `Sitemap found — ${currentPageCount} live pages loaded from it`);
        } else if (crawlVia === "spider") {
          lines.splice(
            1,
            0,
            crawlCount <= 1
              ? "⚠ No sitemap found — only the homepage was reachable. On the Setup tab, paste the sitemap URL into \"Current site pages\" and click Load Pages."
              : `⚠ No sitemap found — crawled ${crawlCount} pages by following links instead (some pages may be missed). On the Setup tab, paste the sitemap URL into "Current site pages" to load the full list.`
          );
        } else if (crawlVia === "failed" && targets.length === 0) {
          lines.splice(
            1,
            0,
            "⚠ Couldn't reach the live site at all (blocked or offline). On the Setup tab, paste the sitemap URL into \"Current site pages\" and click Load Pages, then scan again."
          );
        }
      }
      if (gscRan) {
        lines.push(`${gscAdded} added from Search Console`);
        if (gscSitemapAdded > 0) {
          lines.push(`${gscSitemapAdded} more from GSC sitemaps (not-indexed pages)`);
        }
        lines.push(`${gscUpdated} confirmed in both`);
      }
      scanSummaryCache.set(client.id, lines);
      setScanSummary(lines);
      setStep("review");
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : "Wayback scan failed",
        { kind: "error" }
      );
    } finally {
      setScanning(false);
    }
  }

  async function handleImportSitemap(replace: boolean) {
    // chunk3z-fix516: allow Load Pages with a BLANK input — the backend probes
    // sitemap_index.xml / sitemap.xml / robots.txt from the client's domain when
    // no url or xml is given (the same discovery the scan uses). Previously this
    // hard-required a URL or paste, so "just find the sitemap" wasn't possible
    // from this button. We still need a domain to probe against, though.
    if (!sitemapUrl.trim() && !sitemapXml.trim() && !domain.trim()) {
      toast.show(
        "Enter the site domain (or paste a sitemap URL / XML) first",
        { kind: "error" }
      );
      return;
    }
    setImporting(true);
    setSitemapError(null);
    try {
      const res = await importSitemap(client.id, {
        // Blank url → backend auto-discovers from the stored domain.
        url: sitemapUrl.trim() || undefined,
        xml: sitemapXml.trim() || undefined,
        replace,
        // chunk3z-fix412: persist whatever is in the form so the reload that
        // follows doesn't lose it.
        launchDate: notLaunched ? null : launchDate.trim() || null,
      });
      toast.show(
        res.imported > 0
          ? `Imported ${res.imported} pages (${res.total} total)` +
              (res.locationScopedOut && res.locationScopedOut > 0
                ? ` — scoped to ${res.locationPrefix ?? "this location"}, ${res.locationScopedOut} other-location pages excluded`
                : "")
          : "No sitemap found to import — paste the sitemap URL or its XML above",
        { kind: res.imported > 0 ? "success" : "info" }
      );
      // fix599: when a sitemap was found (typed or auto-discovered), fill the
      // "Current site pages" field with the actual URL so the operator can see
      // exactly what was loaded — and knows they don't need "Insert Yoast".
      if (res.imported > 0 && res.resolvedSitemapUrl) {
        setSitemapUrl(res.resolvedSitemapUrl);
      }
      setSitemapXml("");
      await load();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Sitemap import failed";
      setSitemapError(msg);
      toast.show(msg, { kind: "error" });
    } finally {
      setImporting(false);
    }
  }

  // chunk3z-fix444: discover current-site pages with our own crawler when the
  // client has no usable sitemap (or a capped generator missed pages).
  async function handleCrawlSitemap() {
    setImporting(true);
    setSitemapError(null);
    try {
      const res = await crawlSitemap(client.id, {
        url: sitemapUrl.trim() || domain.trim() || undefined,
        replace: true,
        launchDate: notLaunched ? null : launchDate.trim() || null,
      });
      toast.show(
        `Crawled ${res.imported} pages (${res.total} total)` +
          (res.via === "spider" ? " — via link crawl (no sitemap found)" : "") +
          (res.locationScopedOut && res.locationScopedOut > 0
            ? ` — scoped to ${res.locationPrefix ?? "this location"}, ${res.locationScopedOut} other-location pages excluded`
            : ""),
        { kind: "success" }
      );
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Crawl failed";
      setSitemapError(msg);
      toast.show(msg, { kind: "error" });
    } finally {
      setImporting(false);
    }
  }

  // chunk3z-fix388: re-derive generations from stored data (no archive.org
  // round-trip) after importing a sitemap or editing either boundary date.
  async function handleGscConnect() {
    try {
      const { url } = await getGscConnectUrl();
      window.open(url, "_blank", "width=520,height=680");
      toast.show("Approve access in the Google window, then click Refresh", {
        kind: "info",
      });
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : "Could not start Google sign-in",
        { kind: "error" }
      );
    }
  }

  async function handleGscRefresh() {
    setGscError(null);
    const s = await getGscStatus().catch(() => null);
    if (s) setGsc(s);
    if (!s?.connected) return;
    setGscLoadingProps(true);
    try {
      const { properties } = await getGscProperties();
      setGscProps(properties.map((p) => p.siteUrl));
      if (properties.length === 0) {
        setGscError(
          "Connected, but this Google account can't see any Search Console properties. Check you signed in with the account that has access."
        );
        return;
      }
      // chunk3z-fix428: preselect EVERY property matching this client — both the
      // domain property and any URL-prefix ones — since each may hold history
      // the other lacks.
      const host = domain
        .replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
        .replace(/\/.*$/, "")
        .toLowerCase();
      const hits = properties
        .map((p) => p.siteUrl)
        .filter((u) => u.toLowerCase().includes(host));
      if (hits.length > 0) setGscSites(hits);
    } catch (err) {
      // Previously swallowed, which left the dropdown reading "Loading…"
      // forever with no indication of why.
      setGscError(
        err instanceof Error
          ? err.message
          : "Could not list Search Console properties."
      );
    } finally {
      setGscLoadingProps(false);
    }
  }

  // chunk3z-fix430: without this there was no way to switch accounts — if the
  // wrong Google account authorised (easy to do, since the browser offers
  // whichever is signed in first), you were stuck with its permissions.
  async function handleGscDisconnect() {
    try {
      await disconnectGsc();
      setGsc((g) => ({ ...g, connected: false }));
      setGscProps([]);
      setGscSites([]);
      setGscError(null);
      toast.show("Disconnected. Click Connect Google to sign in again.", {
        kind: "info",
      });
    } catch {
      toast.show("Could not disconnect", { kind: "error" });
    }
  }

  // chunk3z-fix455: core GSC import, callable from both the standalone link and
  // the combined Scan Site History flow. Returns a summary; the caller handles
  // toast/navigation. Imports only selected+visible properties.
  async function runGscImport(): Promise<{
    ran: boolean;
    added: number;
    updated: number;
    sitemapAdded: number;
    properties: number;
    failures: string[];
  }> {
    const toImport = gscVisibleSelected;
    if (toImport.length === 0) {
      return { ran: false, added: 0, updated: 0, sitemapAdded: 0, properties: 0, failures: [] };
    }
    let added = 0;
    let updated = 0;
    let sitemapAdded = 0;
    const failures: string[] = [];
    for (const site of toImport) {
      try {
        const res = await importGsc(client.id, { siteUrl: site });
        added += res.added;
        updated += res.updated;
        sitemapAdded += res.sitemapAdded ?? 0;
      } catch (err) {
        failures.push(`${site}: ${err instanceof Error ? err.message : "failed"}`);
      }
    }
    return { ran: true, added, updated, sitemapAdded, properties: toImport.length, failures };
  }

  async function handleRecompute() {
    setRecomputing(true);
    try {
      const res = await recomputeGenerations(client.id, {
        launchDate: notLaunched ? null : launchDate.trim() || null,
        prevRelaunch: prevRelaunch.trim() || null,
      });
      if (res.prevRelaunch) setPrevRelaunch(res.prevRelaunch);
      toast.show(
        `Generations updated${res.livePaths ? ` — ${res.livePaths} pages confirmed live from the sitemap` : ""}`,
        { kind: "success" }
      );
      await load();
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : "Could not update generations",
        { kind: "error" }
      );
    } finally {
      setRecomputing(false);
    }
  }

  async function loadRedirects(): Promise<number> {
    try {
      const data = await getRedirects(client.id);
      setRedirects(data.redirects);
      setVerify(data.verify);
      return data.redirects.length;
    } catch {
      /* none yet */
      return 0;
    }
  }

  // chunk3z-fix510: save a manual page/blog/service move so it survives a
  // rescan. Fire-and-forget with a quiet failure toast — the local state already
  // updated, so a network hiccup doesn't block the UI; it just won't persist.
  async function persistOverride(
    path: string,
    category: "page" | "blog" | "service"
  ) {
    try {
      await setMigrationUrlOverride(client.id, path, category);
    } catch {
      toast.show("Move shown, but couldn't be saved for next time", {
        kind: "error",
      });
    }
  }

  async function handleBuildRedirects() {
    setBuildElapsed(0);
    setBuilding(true);
    try {
      const res = await buildRedirects(client.id, {
        defaultLocation: defaultLocation || null,
      });
      setLocations(res.locations ?? null);
      toast.show(
        `Matched ${res.matched} redirects · ${res.unmatched} need a target`,
        { kind: "success" }
      );
      await loadRedirects();
      await loadReport();
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : "Could not build redirects",
        { kind: "error" }
      );
    } finally {
      setBuilding(false);
    }
  }

  async function loadReport() {
    try {
      const r = await getMissingPages(client.id);
      setReport(r.text);
      setReportCounts(r.counts);
    } catch {
      /* needs a scan first */
    }
  }

  async function setRedirectTarget(r: MigrationRedirect, targetUrl: string) {
    setRedirects((prev) =>
      prev.map((x) =>
        x.id === r.id
          ? { ...x, targetUrl, status: "confirmed", needsReview: false }
          : x
      )
    );
    try {
      await updateRedirect(r.id, { targetUrl, status: "confirmed" });
      await loadRedirects();
    } catch {
      toast.show("Could not save that target", { kind: "error" });
    }
  }

  // chunk3z-fix422: confirm a guessed target as-is; clears the warning.
  async function markRedirectOk(r: MigrationRedirect) {
    setRedirects((prev) =>
      prev.map((x) => (x.id === r.id ? { ...x, needsReview: false } : x))
    );
    try {
      await updateRedirect(r.id, { needsReview: false, status: "confirmed" });
    } catch {
      toast.show("Could not confirm that row", { kind: "error" });
    }
  }

  async function skipRedirect(r: MigrationRedirect) {
    setRedirects((prev) =>
      prev.map((x) => (x.id === r.id ? { ...x, status: "skipped" } : x))
    );
    try {
      await updateRedirect(r.id, { status: "skipped" });
      await loadRedirects();
    } catch {
      toast.show("Could not skip that row", { kind: "error" });
    }
  }

  // chunk3z-fix461: remove a row from the map entirely — gone from the table and
  // the CSV. Distinct from "404" (a deliberate skip that stays as a record).
  async function removeRedirect(r: MigrationRedirect) {
    // Optimistic — drop it immediately, then persist the delete.
    setRedirects((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await deleteRedirect(r.id);
    } catch {
      toast.show("Could not remove that row", { kind: "error" });
      await loadRedirects(); // restore on failure
    }
  }

  async function toggleJunk(u: MigrationUrl) {
    // Optimistic — the row flips immediately, then persists.
    setUrls((prev) =>
      prev.map((x) => (x.id === u.id ? { ...x, isJunk: !x.isJunk } : x))
    );
    try {
      await setMigrationUrlJunk(u.id, !u.isJunk);
    } catch {
      setUrls((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, isJunk: u.isJunk } : x))
      );
      toast.show("Could not update that row", { kind: "error" });
    }
  }

  // chunk3z-fix390: the scan is only meaningful once we know how to classify
  // what it finds. A launched client needs a launch date (the Gen1/Gen2
  // boundary) and the current sitemap (which confirms what is still live). A
  // pre-launch prospect needs neither — everything found is their live site.
  // chunk3z-fix431: properties whose host contains the client's domain.
  const gscHost = domain
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "")
    .toLowerCase();
  const gscMatching = gscHost
    ? gscProps.filter((p) => p.toLowerCase().includes(gscHost))
    : gscProps;
  const gscVisible = gscShowAll ? gscProps : gscMatching;
  // chunk3z-fix436: the import acts on properties that are BOTH selected and
  // currently visible. Selecting "All (414)" then narrowing back to this client
  // must not leave 414 silently queued — the count and the Import button always
  // reflect what's on screen.
  const gscVisibleSelected = gscVisible.filter((p) => gscSites.includes(p));
  const gscAllVisibleSelected =
    gscVisible.length > 0 && gscVisibleSelected.length === gscVisible.length;

  const genFilter: "all" | "previous" = migration?.launchDate
    ? "previous"
    : "all";
  const hasSitemap = targets.length > 0;
  const canScan = Boolean(
    domain.trim() && (notLaunched || (launchDate.trim() && hasSitemap))
  );
  const missingReason = !domain.trim()
    ? "Enter the client domain to continue."
    : notLaunched
      ? ""
      : !launchDate.trim() && !hasSitemap
        ? "Add the launch date and load the current site pages first."
        : !launchDate.trim()
          ? "Add the launch date first — it separates our pages from the previous site's."
          : "Load the current site pages first — they confirm which old URLs are still live.";

  // chunk3z-fix478: for a NOT-LAUNCHED client, "Pages found" should show only
  // the pages we're responsible for transferring — the CURRENT live site. That's
  // the set the crawl found (migration_targets) plus any archived URL that still
  // has Search Console activity (so it clearly still resolves today). Older
  // pre-current-site history is redirect-only and hidden here. This filter
  // affects DISPLAY only; the redirect build still covers every URL server-side.
  // chunk3z-fix514: key the current-site set (lowercased, no trailing slash) so
  // it reconciles crawler targets ("/foo") with wayback/GSC urls ("/foo/"). The
  // raw-path Set silently missed every page whose two sources disagreed on the
  // trailing slash — which hid real sitemap pages from Pages found.
  const currentSitePaths = useMemo(
    () => new Set(targets.map((t) => pathKey(t.path))),
    [targets]
  );

  const visibleUrls = useMemo(() => {
    const q = search.trim().toLowerCase();
    return urls.filter((u) => {
      if (!showJunk && u.isJunk) return false;
      // chunk3z-fix478/495: not-launched → restrict Pages found to the current
      // site. A page counts as current only if it's in the crawl/sitemap targets
      // (the authoritative "what exists now" list). We deliberately do NOT treat
      // "has GSC impressions" as proof of being live: GSC retains URLs for a
      // while after they 404, so a stale indexed page (e.g. /what-is-a-dental-
      // buccal-summary that now returns Page-not-found) would otherwise show as a
      // current page and its ↗ link would 404. Those still get redirects as old
      // URLs — they're just not shown as pages to transfer.
      //
      // chunk3z-fix508: BUT when the operator explicitly turns on "Show junk",
      // they're asking to audit what got filtered — the whole point is to catch
      // a real page wrongly flagged as junk. Junk rows are archive/GSC-only by
      // nature, so the current-site restriction above hid EVERY filtered-junk
      // row for a not-launched client, making "Show junk" show almost nothing.
      // So: a junk row bypasses the current-site restriction while Show junk is
      // on. Non-junk rows are still held to the current site.
      if (notLaunched && targets.length > 0) {
        const exemptAsJunkAudit = showJunk && u.isJunk;
        if (!exemptAsJunkAudit && !currentSitePaths.has(pathKey(u.path))) return false;
      }
      // chunk3z-fix392: "All Previous" = every generation that is NOT our
      // current site (gen2 + gen3 + anything older). This is the set the
      // redirect file needs — the workflow includes ALL old generations for
      // maximum SEO capture, so filtering them one at a time was busywork.
      if (genFilter === "previous") {
        if (!u.generation || u.generation === "gen1") return false;
      } else if (genFilter !== "all" && u.generation !== genFilter) {
        return false;
      }
      if (q && !u.path.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [urls, showJunk, genFilter, search, notLaunched, targets, currentSitePaths]);

  // chunk3z-fix405: sort the filtered rows. Dates sort chronologically (the
  // stored YYYY-MM-DD strings compare correctly), generations sort by age
  // rather than alphabetically, and empty values always sink to the bottom so
  // they never crowd out real data at the top of the table.
  const sortedUrls = useMemo(() => {
    const genRank: Record<string, number> = { gen1: 0, gen2: 1, gen3: 2 };
    const rows = [...visibleUrls];
    rows.sort((a, b) => {
      // fix684: when "Show junk" is on, the junk rows are what the operator is
      // there to review, so float them all to the top regardless of the active
      // column sort. Within the junk block and within the non-junk block, the
      // normal sort below still applies. (No effect when Show junk is off, since
      // junk rows aren't visible then.)
      if (showJunk) {
        const ja = a.isJunk ? 0 : 1;
        const jb = b.isJunk ? 0 : 1;
        if (ja !== jb) return ja - jb;
      }
      let cmp = 0;
      if (sortKey === "path") {
        cmp = a.path.localeCompare(b.path);
      } else if (sortKey === "generation") {
        const ra = a.generation ? genRank[a.generation] ?? 99 : 99;
        const rb = b.generation ? genRank[b.generation] ?? 99 : 99;
        cmp = ra - rb;
        if (cmp === 0) cmp = a.path.localeCompare(b.path);
      } else if (sortKey === "source") {
        // chunk3z-fix434: Both first (most corroborated), then GSC-only
        // (the Wayback-missed recoveries), then Archive-only.
        const srcRank: Record<string, number> = { both: 0, gsc: 1, wayback: 2 };
        const ra = srcRank[a.source] ?? 99;
        const rb = srcRank[b.source] ?? 99;
        cmp = ra - rb;
        if (cmp === 0) cmp = a.path.localeCompare(b.path);
      } else {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (!av && !bv) cmp = 0;
        else if (!av) return 1; // missing dates sink regardless of direction
        else if (!bv) return -1;
        else cmp = av.localeCompare(bv);
        if (cmp === 0) cmp = a.path.localeCompare(b.path);
      }
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  }, [visibleUrls, sortKey, sortAsc, showJunk]);

  // chunk3z-fix453: group current-site pages into SERVICES / OTHER / BLOG POSTS
  // for the target dropdown, each group sorted by path. A flat list of every
  // page is hard to scan when picking a redirect target.
  const groupedTargets = useMemo(() => {
    const groups: Record<TargetCategory, MigrationTarget[]> = {
      services: [],
      other: [],
      blog: [],
    };
    for (const t of targets) {
      groups[categorizeTarget(t.path, t.sitemapType)].push(t);
    }
    for (const key of CATEGORY_ORDER) {
      groups[key].sort((a, b) => a.path.localeCompare(b.path));
    }
    // chunk3z-fix470: pin the blog HOME (/blog/, /news/, etc.) to the top of the
    // BLOG POSTS group — it's the most common pick when mapping a blog post, so
    // it should be one glance away, not buried alphabetically.
    const isBlogHome = (p: string) => {
      const s = p.toLowerCase().replace(/\/+$/, "").replace(/^\//, "");
      return /^(blogs?|news|articles?|our-blog|dental-blog)$/.test(s);
    };
    groups.blog.sort((a, b) => {
      const ah = isBlogHome(a.path) ? 0 : 1;
      const bh = isBlogHome(b.path) ? 0 : 1;
      if (ah !== bh) return ah - bh;
      return a.path.localeCompare(b.path);
    });
    return groups;
  }, [targets]);

  // chunk3z-fix457: order the redirect map so rows still NEEDING review float to
  // the top and confirmed rows sink to the bottom. When you hit "OK" on the top
  // row, it drops away and the next unreviewed row slides up into the same spot —
  // so you can keep the mouse still and confirm a run of correct rows in place.
  // Within each group, keep a stable source-path order so nothing shuffles
  // unpredictably. Skipped rows sink furthest.
  // fix728: freeze the DISPLAY ORDER so picking a target on a row doesn't make it
  // jump. Previously the sort ranked live `needsReview`, so the instant you chose
  // a target the row's rank flipped (0→1) and it leapt to the bottom, yanking the
  // page. We now compute the order from a SNAPSHOT that only refreshes when the
  // set of rows changes (rebuild/load) — not on every per-row edit. Editing a row
  // updates its contents in place; the order holds until the next rebuild.
  const orderSnapshotRef = useRef<Map<string, number>>(new Map());
  const orderSignature = redirects
    .map((r) => r.sourcePath)
    .sort()
    .join("|");
  useMemo(() => {
    // Rebuild the frozen order whenever the ROW SET changes (not their review
    // state). Rank by needsReview at snapshot time so the initial layout still
    // floats unreviewed rows to the top.
    const rank = (r: MigrationRedirect) =>
      r.status === "skipped" ? 2 : r.needsReview ? 0 : 1;
    const ordered = [...redirects].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      return a.sourcePath.localeCompare(b.sourcePath);
    });
    const m = new Map<string, number>();
    ordered.forEach((r, i) => m.set(r.sourcePath, i));
    orderSnapshotRef.current = m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSignature]);

  const displayRedirects = useMemo(() => {
    const order = orderSnapshotRef.current;
    return [...redirects].sort((a, b) => {
      const ia = order.get(a.sourcePath) ?? Number.MAX_SAFE_INTEGER;
      const ib = order.get(b.sourcePath) ?? Number.MAX_SAFE_INTEGER;
      if (ia !== ib) return ia - ib;
      return a.sourcePath.localeCompare(b.sourcePath);
    });
  }, [redirects]);

  function toggleSort(key: typeof sortKey) {
    if (sortKey === key) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      // Paths read best A-Z; dates and generations are most useful newest or
      // oldest first, so start those descending.
      setSortAsc(key === "path");
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
            <line x1="8" y1="2" x2="8" y2="18" />
            <line x1="16" y1="6" x2="16" y2="22" />
          </svg>
        </div>
        <div>
          <h1 className="text-text font-semibold text-xl">Sitemaps</h1>
          <p className="text-text-muted text-sm mt-0.5">
            Map a site's URL history against its current pages, ready for 301
            redirects.
          </p>
        </div>
      </div>

      {/* Step nav — mirrors the Content Generation sub-tabs */}
      {/* chunk3z-fix394: sticky sub-nav on its own card, matching the Content
          Generation tab's Build Queue / Outlines / Generate strip so the two
          tabs feel like the same app. */}
      <div className="sticky top-[40px] z-20 -mx-6 px-6 pt-2 pb-0 mb-4 bg-bg-subtle">
        <div className="flex items-center gap-1 bg-bg-card rounded-lg shadow-card border border-border px-2">
          {STEPS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setStep(s.key)}
              className={`flex items-center gap-2 px-3.5 py-2.5 my-1.5 text-sm rounded-md font-medium transition ${
                step === s.key
                  ? "bg-accent/10 text-accent"
                  : "text-text-muted hover:text-text hover:bg-bg-subtle/60"
              }`}
            >
              <span
                className={`inline-flex items-center justify-center w-5 h-5 rounded text-xs font-semibold ${
                  step === s.key
                    ? "bg-accent text-white"
                    : "bg-bg-subtle text-text-muted"
                }`}
              >
                {s.n}
              </span>
              {s.label}
            </button>
          ))}
          {counts && (
            <span className="ml-auto text-xs text-text-muted pr-2">
              {/* chunk3z-fix480: for a not-launched client there's no "old vs
                  current" split — it's all one site. Show a single total. */}
              {notLaunched
                ? `${counts.kept} pages found`
                : `${counts.kept} old URLs · ${counts.targets} current pages`}
            </span>
          )}
        </div>
      </div>

      {/* chunk3z-fix507: scan summary as an INLINE banner directly below the
          Setup/Review/Redirects step bar — not the fix496 floating popup, which
          overlapped page content. Review step only, client-scoped, and sticky
          until dismissed with ×. Numbers match the counts strip because both are
          built from the final counts after load. */}
      {scanSummary && step === "review" && (
        <div className="bg-success-bg border border-success rounded-lg p-4 shadow-card flex items-start gap-3">
          <span className="text-success text-base mt-0.5">✓</span>
          {/* chunk3z-fix518: render each summary line as its own element so the
              sitemap-status line can be emphasized. Pulling the current site's
              pages from its SITEMAP (not Wayback/GSC history) is the signal that
              matters most — the sitemap is the authoritative "what's live now"
              list, so a found sitemap gets bold + a highlighted row, and a miss
              gets a bold warning color. Everything else stays plain. */}
          <div className="flex-1 text-sm text-text leading-snug space-y-0.5">
            {scanSummary.map((line, i) => {
              const isSitemapHit = line.startsWith("Sitemap found");
              const isSitemapMiss = line.startsWith("⚠");
              if (isSitemapHit) {
                // chunk3z-fix523: plain bold text, no card background or border —
                // just make it stand out by weight against the green box.
                return (
                  <div key={i} className="font-bold text-text">
                    {line}
                  </div>
                );
              }
              if (isSitemapMiss) {
                return (
                  <div key={i} className="font-bold text-warning">
                    {line}
                  </div>
                );
              }
              return <div key={i}>{line}</div>;
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              scanSummaryCache.delete(client.id);
              setScanSummary(null);
            }}
            className="text-text-muted hover:text-text text-lg leading-none px-1 -mr-1 rounded hover:bg-bg-subtle transition-colors shrink-0"
            aria-label="Dismiss scan summary"
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="bg-bg-card border border-border rounded-lg p-8 text-center text-text-muted text-sm shadow-card">
          Loading…
        </div>
      ) : step === "setup" ? (
        <div className="space-y-4">
        <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card space-y-3">
          {/* chunk3z-fix393: compact two-column setup. Domain + launch state sit
              on one row, the sitemap on the next, so the whole form fits above
              the fold instead of stacking three full-width blocks. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            {/* Domain */}
            <Field label="Client domain" hint="Prefilled from the client record.">
              <TextInput
                value={domain}
                onChange={setDomain}
                placeholder="example.com"
              />
            </Field>

            {/* Launch state */}
            <div>
              <label className="block text-text text-sm font-medium mb-1">
                Launch date
              </label>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="date"
                  value={launchDate}
                  disabled={notLaunched}
                  onChange={(e) => {
                    setLaunchDate(e.target.value);
                    setLaunchDateTouched(true);
                  }}
                  className="bg-bg-input border border-border rounded-md px-3 py-1.5 text-sm text-text focus:outline-none focus:border-accent disabled:opacity-40"
                />
                <label className="flex items-center gap-1.5 text-sm text-text-muted whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={notLaunched}
                    onChange={(e) => setNotLaunched(e.target.checked)}
                  />
                  Not launched yet
                </label>
              </div>
              <p className="text-xs text-text-subtle mt-1">
                {notLaunched
                  ? "Onboarding client — everything found is their current site."
                  : "The day our site went live. Splits our pages from the previous site's."}
              </p>

            </div>

            {/* Sitemap — spans both columns */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-1">
                <label className="text-text text-sm font-medium">
                  Current site pages
                </label>
                <span className="text-text-subtle text-sm" aria-hidden="true">|</span>
                {/* chunk3z-fix521: sits directly beside the label. One-click fill
                    of the Yoast/RankMath index URL. fix599: once a sitemap has
                    been loaded (targets exist), this hint is no longer needed —
                    render it very light and non-interactive so the operator knows
                    they don't have to insert anything. */}
                {targets.length > 0 ? (
                  <span
                    className="text-sm text-text-subtle/50 cursor-default select-none"
                    title="A sitemap was already found and loaded — no need to insert one."
                  >
                    Sitemap loaded ✓
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSitemapUrl(guessSitemapUrl(domain))}
                    disabled={!domain.trim()}
                    className="text-sm text-accent hover:underline disabled:text-text-subtle disabled:no-underline disabled:cursor-not-allowed"
                    title="Fill in the standard Yoast / RankMath sitemap index URL for this domain"
                  >
                    Insert Yoast Sitemap
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <TextInput
                  value={sitemapUrl}
                  onChange={setSitemapUrl}
                  placeholder="https://example.com/sitemap_index.xml"
                />
                <Button
                  variant="secondary"
                  onClick={() => handleImportSitemap(true)}
                  disabled={importing}
                  // chunk3z-fix421: as a flex child next to the URL input the
                  // button was being squeezed until its label wrapped onto two
                  // lines. shrink-0 + nowrap keeps it on one.
                  className="shrink-0 whitespace-nowrap"
                >
                  {importing ? "Loading…" : "Load Pages"}
                </Button>
                {/* chunk3z-fix444: in-house crawl fallback — no 500-page cap,
                    finds pages a capped generator or a broken sitemap misses. */}
                <span
                  title="No sitemap, or it's missing pages? Crawl the live site to discover pages directly."
                  className="shrink-0 flex"
                >
                  <Button
                    variant="secondary"
                    onClick={handleCrawlSitemap}
                    disabled={importing}
                    className="whitespace-nowrap"
                  >
                    {importing ? "…" : "Crawl site"}
                  </Button>
                </span>
              </div>
              {sitemapError && (
                <p className="mt-1.5 rounded-md border border-danger-border bg-danger-bg px-2.5 py-1.5 text-xs text-danger">
                  {sitemapError}
                </p>
              )}
              <p className="text-xs text-text-subtle mt-1">
                Works with any XML sitemap — Yoast, Rank Math, Shopify,
                Squarespace, Wix, Webflow, or a plain sitemap.xml. Taxonomy and
                media sitemaps are skipped automatically.
                {targets.length > 0 && (
                  <span className="text-success font-medium">
                    {" "}
                    {targets.length} pages loaded.
                  </span>
                )}
              </p>
              <details className="text-xs text-text-muted mt-1">
                <summary className="cursor-pointer select-none">
                  Site not public yet? Paste the sitemap instead
                </summary>
                <div className="mt-2 space-y-2">
                  <textarea
                    value={sitemapXml}
                    onChange={(e) => setSitemapXml(e.target.value)}
                    rows={4}
                    placeholder="Paste <urlset>…</urlset>, a <sitemapindex>, or one URL per line"
                    className="w-full bg-bg-input border border-border rounded-md px-3 py-2 text-sm text-text font-mono focus:outline-none focus:border-accent"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => handleImportSitemap(true)}
                    disabled={importing}
                  >
                    Load Pasted Sitemap
                  </Button>
                </div>
              </details>
            </div>

            {/* chunk3z-fix424: optional Search Console cross-check. GSC knows
                which URLs Google actually indexed, which regularly includes
                pages Wayback never captured — those would otherwise get no
                redirect at all. */}
            <div className="md:col-span-2 border-t border-border-subtle pt-3">
              <span className="block text-text text-sm font-medium mb-1">
                Search Console{" "}
                <span className="text-text-subtle font-normal">(optional)</span>
              </span>
              <p className="text-xs text-text-subtle mb-2">
                Cross-checks the archive scan and finds indexed URLs Wayback
                missed.
              </p>
              {!gsc.configured ? (
                <p className="text-xs text-text-muted">
                  Add the Search Console client ID and secret in Settings to
                  enable this.
                </p>
              ) : !gsc.connected ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="secondary" onClick={handleGscConnect}>
                    Connect Google
                  </Button>
                  <button
                    type="button"
                    onClick={handleGscRefresh}
                    className="text-xs text-accent hover:underline"
                  >
                    I&apos;ve approved it — refresh
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Status bar — connection state left, actions right */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
                      <span className="h-1.5 w-1.5 rounded-full bg-success" />
                      Connected
                    </span>
                    <span className="ml-auto flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleGscRefresh}
                        className="text-xs text-accent hover:underline"
                      >
                        {gscLoadingProps ? "Loading…" : "Reload properties"}
                      </button>
                      <button
                        type="button"
                        onClick={handleGscDisconnect}
                        className="text-xs text-text-muted hover:text-danger"
                        title="Sign out so you can connect a different Google account"
                      >
                        Disconnect
                      </button>
                    </span>
                  </div>

                  {gscProps.length > 0 && (
                    <div className="rounded-lg border border-border bg-bg-subtle overflow-hidden">
                      {/* Header row: what's shown + select-all + scope toggle */}
                      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-bg-card">
                        <span className="text-xs font-medium text-text">
                          Properties to import
                        </span>
                        <span className="text-xs text-text-subtle">
                          {gscVisibleSelected.length} of {gscVisible.length}{" "}
                          selected
                        </span>
                        <span className="ml-auto flex items-center gap-3 text-xs">
                          {gscVisible.length > 0 && (
                            <button
                              type="button"
                              onClick={() =>
                                setGscSites((cur) =>
                                  gscAllVisibleSelected
                                    ? // deselect only the visible ones
                                      cur.filter((p) => !gscVisible.includes(p))
                                    : // add every visible one not already picked
                                      [
                                        ...cur,
                                        ...gscVisible.filter(
                                          (p) => !cur.includes(p)
                                        ),
                                      ]
                                )
                              }
                              className="text-accent hover:underline"
                            >
                              {gscAllVisibleSelected ? "Clear all" : "Select all"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setGscShowAll((v) => !v)}
                            className="text-text-muted hover:text-accent hover:underline"
                          >
                            {gscShowAll
                              ? "This client only"
                              : `All (${gscProps.length})`}
                          </button>
                        </span>
                      </div>

                      {/* Property cards */}
                      <div className="max-h-44 overflow-auto divide-y divide-border-subtle">
                        {gscVisible.length === 0 && (
                          <p className="text-xs text-text-muted px-3 py-3">
                            No properties match {gscHost}. Use &ldquo;All&rdquo; if
                            it&apos;s verified under a different domain.
                          </p>
                        )}
                        {gscVisible.map((pr) => {
                          const checked = gscSites.includes(pr);
                          const meta = gscPropMeta(pr);
                          return (
                            <label
                              key={pr}
                              className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                                checked ? "bg-accent-subtle" : "hover:bg-bg-hover"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) =>
                                  setGscSites((cur) =>
                                    e.target.checked
                                      ? [...cur, pr]
                                      : cur.filter((x) => x !== pr)
                                  )
                                }
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm text-text font-mono truncate">
                                  {meta.label}
                                </span>
                              </span>
                              <span
                                className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${meta.cls}`}
                                title={meta.title}
                              >
                                {meta.badge}
                              </span>
                            </label>
                          );
                        })}
                      </div>

                      {/* Footer: explainer only (chunk3z-fix459). GSC is pulled
                          as part of Scan Site History, in order after Wayback —
                          no separate import action needed. Re-running the scan
                          re-pulls GSC too, so a standalone button was redundant
                          and visually competed with the property rows. */}
                      <div className="px-3 py-2.5 border-t border-border-subtle bg-bg-card">
                        <p className="text-xs text-text-subtle">
                          Domain and URL-prefix properties hold different history
                          — select both. Pulled automatically with Scan Site
                          History; 16 months of Performance pages, merged and
                          deduped.
                        </p>
                      </div>
                    </div>
                  )}

                  {gscError && <p className="text-xs text-danger">{gscError}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Run */}
          <div className="border-t border-border-subtle pt-3 flex items-center gap-3 flex-wrap">
            <Button
              variant="primary"
              onClick={handleScan}
              disabled={scanning || !canScan}
            >
              {scanning
                ? notLaunched
                  ? "Crawling + scanning…"
                  : "Scanning archive.org…"
                : notLaunched
                  ? gsc.connected && gscVisibleSelected.length > 0
                    ? "Crawl + Scan History + GSC"
                    : "Crawl + Scan History"
                  : gsc.connected && gscVisibleSelected.length > 0
                    ? "Scan Site History + GSC"
                    : "Scan Site History"}
            </Button>
            {urls.length > 0 && (
              <Button
                variant="secondary"
                onClick={handleRecompute}
                disabled={recomputing}
              >
                {recomputing ? "Re-sorting…" : "Re-sort by Generation"}
              </Button>
            )}
            {!canScan && !scanning && (
              <span className="text-xs text-text-muted">{missingReason}</span>
            )}
            {scanning && (
              <span className="text-xs text-text-muted">
                Large sites can take a minute or two.
              </span>
            )}
            {migration?.lastError && !scanning && (
              <span className="text-xs text-danger">
                Last error: {migration.lastError}
              </span>
            )}
          </div>
        </div>

        {/* fix629: cross-domain redirects is its own standalone section below
            the setup card — previously it sat inside the same white card as the
            scan button, which visually lumped them together. */}
        <CrossDomainRedirects client={client} newDomain={domain} />
        </div>
      ) : step === "review" ? (
        <div className="space-y-4">
          {/* fix634: this Review step is a diagnostic — it's where the scan gets
              sanity-checked before the map is built (generation split, junk,
              recovery sources). It's not the deliverable. Point anyone who lands
              here (esp. the web team) to Redirects & Report, which is what they
              actually use. */}
          <div className="bg-accent-subtle border border-accent rounded-lg px-4 py-3 flex items-start gap-3 text-sm">
            <span className="text-accent mt-0.5">ℹ</span>
            <div className="flex-1">
              <span className="text-text">
                This step is a diagnostic for building the redirect map. If you're
                here to grab the redirects,{" "}
              </span>
              <button
                type="button"
                onClick={() => setStep("redirects")}
                className="text-accent font-medium underline hover:no-underline"
              >
                go to Redirects &amp; Report
              </button>
              <span className="text-text">.</span>
            </div>
          </div>
          {/* Counts strip */}
          {counts && (
            <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card flex flex-col gap-3 text-sm">
              <div className="flex flex-wrap items-center gap-4">
                <Stat
                  label={
                    migration && !migration.launchDate
                      ? "Pages found"
                      : "Old URLs kept"
                  }
                  value={counts.kept}
                />
                <Stat label="Filtered junk" value={counts.junk} muted />
                <Stat label="Current pages" value={counts.targets} />
                <span className="ml-auto flex items-center gap-2 text-xs">
                  {migration && !migration.launchDate ? (
                    <span className="rounded px-2 py-0.5 bg-accent-subtle text-accent text-[11px] font-medium">
                      Pre-launch · their current site (becomes Gen 2 at launch)
                    </span>
                  ) : (
                    <>
                      <GenBadge gen="gen1" /> {counts.gen1}
                      <GenBadge gen="gen2" /> {counts.gen2}
                      <GenBadge gen="gen3" /> {counts.gen3}
                    </>
                  )}
                </span>
              </div>
              {/* chunk3z-fix434: where the kept URLs came from. GSC-only rows are
                  the pages the archive scan missed — the whole point of the
                  Search Console cross-check.
                  chunk3z-fix439: each badge is now wrapped WITH its count and
                  label in one bordered pill, so it's unambiguous which number
                  the colored badge belongs to. */}
              {(counts.srcGsc > 0 || counts.srcBoth > 0) && (
                <div className="flex flex-wrap items-center gap-2 text-xs border-t border-border-subtle pt-2">
                  <span className="text-text-subtle">Recovered from:</span>
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-2 py-1">
                    <SourceBadge source="wayback" />
                    <span className="text-text">
                      {counts.srcWayback}{" "}
                      <span className="text-text-muted">archive only</span>
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-2 py-1">
                    <SourceBadge source="gsc" />
                    <span className="text-text">
                      {counts.srcGsc}{" "}
                      <span className="text-text-muted">GSC only</span>
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-2 py-1">
                    <SourceBadge source="both" />
                    <span className="text-text">
                      {counts.srcBoth}{" "}
                      <span className="text-text-muted">in both</span>
                    </span>
                  </span>
                  {counts.srcGsc > 0 && (
                    <span className="text-text-subtle">
                      · GSC recovered {counts.srcGsc} page
                      {counts.srcGsc === 1 ? "" : "s"} Wayback missed
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Old URLs */}
          <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border">
              <h2 className="text-text font-semibold text-sm">
                {migration && !migration.launchDate
                  ? "Pages found"
                  : "Old URLs"}
              </h2>
              {/* chunk3z-fix401: the generation and path filters are gone. This
                  table always shows Gen 2 and older — the pages that stopped
                  existing and therefore need a redirect or a rebuild. Gen 1
                  pages survived the migration and need neither. */}
              <span className="text-xs text-text-subtle">
                {migration?.launchDate ? "Gen 2 and older" : "All pages found"}
              </span>
              <label className="flex items-center gap-1.5 text-xs text-text-muted ml-2">
                <input
                  type="checkbox"
                  checked={showJunk}
                  onChange={(e) => setShowJunk(e.target.checked)}
                />
                Show junk
              </label>
              {/* chunk3z-fix477: tree vs flat-list toggle. Defaults to the tree
                  (parent/child structure) for a not-launched prospect, where the
                  found pages ARE their site and structure matters most. */}
              <button
                type="button"
                onClick={() => setPagesAsTree((v) => !v)}
                className="ml-auto text-xs text-accent hover:underline"
              >
                {pagesAsTree ? "Show as list" : "Show as structure"}
              </button>
              {!pagesAsTree && (
                <span className="text-xs text-text-muted">
                  {sortedUrls.length} shown
                </span>
              )}
            </div>

            {/* chunk3z-fix478: for a not-launched client, explain that older
                pre-current-site pages are hidden here but still get redirects. */}
            {notLaunched && targets.length > 0 && urls.length > visibleUrls.length && (
              <div className="px-4 py-2 text-xs text-text-muted bg-bg-subtle border-b border-border-subtle">
                Showing the {visibleUrls.length} current-site pages you'll
                transfer. {urls.length - visibleUrls.length} older archived URLs
                are hidden here but still get redirects in the map.
              </div>
            )}

            {pagesAsTree ? (
              <div className="max-h-[28rem] overflow-auto px-4 pb-4">
                <PagesFoundTree
                  urls={visibleUrls}
                  onRemove={toggleJunk}
                  forcedBlog={forcedBlog}
                  forcedService={forcedService}
                  forcedPage={forcedPage}
                  onMoveToBlog={(u) => {
                    setForcedBlog((prev) => new Set(prev).add(u.id));
                    // moving to blog overrides a prior move-to-services / to-page
                    setForcedService((prev) => dropFrom(prev, u.id));
                    setForcedPage((prev) => dropFrom(prev, u.id));
                    void persistOverride(u.path, "blog");
                  }}
                  onMoveToService={(u) => {
                    setForcedService((prev) => new Set(prev).add(u.id));
                    setForcedBlog((prev) => dropFrom(prev, u.id));
                    setForcedPage((prev) => dropFrom(prev, u.id));
                    void persistOverride(u.path, "service");
                  }}
                  onMoveToPage={(u) => {
                    // chunk3z-fix509: pull a mis-sorted post into Pages (root).
                    setForcedPage((prev) => new Set(prev).add(u.id));
                    setForcedBlog((prev) => dropFrom(prev, u.id));
                    setForcedService((prev) => dropFrom(prev, u.id));
                    void persistOverride(u.path, "page");
                  }}
                  onMoveSubtreeToPage={(node) => {
                    // chunk3z-fix539: move a parent AND every descendant to Pages
                    // in one action (e.g. /contact-us + its 15 nested pages that
                    // mis-sorted into Blog). Collect all real URLs in the subtree
                    // and force each to "page".
                    const subtreeUrls = collectUrls(node);
                    setForcedPage((prev) => {
                      const next = new Set(prev);
                      for (const su of subtreeUrls) next.add(su.id);
                      return next;
                    });
                    setForcedBlog((prev) => {
                      let next = prev;
                      for (const su of subtreeUrls) next = dropFrom(next, su.id);
                      return next;
                    });
                    setForcedService((prev) => {
                      let next = prev;
                      for (const su of subtreeUrls) next = dropFrom(next, su.id);
                      return next;
                    });
                    for (const su of subtreeUrls) void persistOverride(su.path, "page");
                  }}
                />
              </div>
            ) : visibleUrls.length === 0 ? (
              <div className="p-8 text-center text-text-muted text-sm">
                {urls.length === 0 ? (
                  "No scan yet — run Scan History first."
                ) : genFilter === "previous" &&
                  urls.every((u) => u.generation === "gen1") ? (
                  // chunk3z-fix458: every scanned URL matches a page on the
                  // CURRENT site (Gen 1), so nothing was replaced — there are no
                  // old pages needing redirects. Say so plainly; "no URLs match
                  // these filters" made this look broken.
                  <>
                    <p className="text-text">
                      Every archived URL still exists on the current site — no
                      redirects needed.
                    </p>
                    <p className="mt-1 text-xs">
                      All {urls.length} pages found are live (Gen 1). If the site
                      was actually relaunched with new URLs, check the launch date
                      above, then re-scan.
                    </p>
                  </>
                ) : (
                  "No URLs match these filters."
                )}
              </div>
            ) : (
              <div className="max-h-[28rem] overflow-auto">
                {/* chunk3z-fix400: table-fixed + explicit column widths. Without
                    it, toggling "Show junk" pulled in long asset paths
                    (/wp-content/uploads/...jpg) which widened the Path column
                    and shifted every other column. */}
                <table className="w-full text-sm table-fixed">
                  <thead className="sticky top-0 z-10 bg-bg-card shadow-[0_1px_0_var(--border)]">
                    <tr className="text-left text-xs text-text-muted">
                      <SortHeader
                        label="Path"
                        col="path"
                        sortKey={sortKey}
                        sortAsc={sortAsc}
                        onSort={toggleSort}
                        className="px-4 py-2"
                      />
                      <SortHeader
                        label="First"
                        col="firstCapture"
                        sortKey={sortKey}
                        sortAsc={sortAsc}
                        onSort={toggleSort}
                        className="px-3 py-2 w-28"
                      />
                      <SortHeader
                        label="Last"
                        col="lastCapture"
                        sortKey={sortKey}
                        sortAsc={sortAsc}
                        onSort={toggleSort}
                        className="px-3 py-2 w-28"
                      />
                      <SortHeader
                        label="Gen"
                        col="generation"
                        sortKey={sortKey}
                        sortAsc={sortAsc}
                        onSort={toggleSort}
                        className="px-3 py-2 w-24"
                      />
                      <SortHeader
                        label="Source"
                        col="source"
                        sortKey={sortKey}
                        sortAsc={sortAsc}
                        onSort={toggleSort}
                        className="px-3 py-2 w-28"
                      />
                      <th className="px-3 py-2 font-medium text-right w-16">
                        Junk
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUrls.map((u) => (
                      <tr
                        key={u.id}
                        className={`border-t border-border-subtle ${
                          u.isJunk ? "opacity-50" : ""
                        }`}
                      >
                        <td className="px-4 py-1.5">
                          <span className="text-text break-all">{u.path}</span>
                          {u.junkReason && (
                            <span className="ml-2 text-xs text-text-subtle">
                              ({u.junkReason})
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-text-muted text-xs whitespace-nowrap">
                          {u.firstCapture ?? "—"}
                        </td>
                        <td className="px-3 py-1.5 text-text-muted text-xs whitespace-nowrap">
                          {u.lastCapture ?? "—"}
                          {/* chunk3z-fix473: a single Wayback snapshot (First ===
                              Last) with no GSC signal is the lowest-value kind of
                              old URL — archived once, never seen again. Flag it so
                              it can be eyeballed and bulk-removed if desired; it's
                              still included in the map by default. */}
                          {u.firstCapture &&
                            u.lastCapture === u.firstCapture &&
                            u.source === "wayback" && (
                              <span
                                className="ml-1.5 text-[10px] text-text-subtle"
                                title="Archived only once, never re-captured, and no Search Console activity — likely a dead page. Still redirected by default; remove it in the map if you don't want it."
                              >
                                · 1 snapshot
                              </span>
                            )}
                        </td>
                        {/* chunk3z-fix400: generation is now read-only — the
                            date-based sorting is trusted, and an editable
                            dropdown per row invited accidental changes. */}
                        <td className="px-3 py-1.5">
                          {u.generation ? (
                            <GenBadge
                              gen={u.generation}
                              prelaunch={!migration?.launchDate}
                            />
                          ) : (
                            <span className="text-text-subtle text-xs">—</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          <SourceBadge
                            source={u.source}
                            gscClicks={u.gscClicks}
                          />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <input
                            type="checkbox"
                            checked={u.isJunk}
                            onChange={() => toggleJunk(u)}
                            title="Mark as junk (reversible — nothing is deleted)"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Current site tree — collapsed by default (chunk3z-fix447).
              chunk3z-fix477: hidden entirely for a not-launched prospect — the
              "Pages found" tree above already shows their site structure, so a
              separate identical panel is redundant. */}
          {showStructure && !notLaunched && (
            <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <h2 className="text-text font-semibold text-sm">
                  Current site structure
                </h2>
                <span className="text-xs text-text-muted">
                  {targets.length} pages
                </span>
              </div>
              {targets.length === 0 ? (
                <div className="p-8 text-center text-text-muted text-sm">
                  No sitemap imported yet — see step 2.
                </div>
              ) : (
                <div className="max-h-[24rem] overflow-auto p-3">
                  <SitemapTree targets={targets} />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            {!notLaunched ? (
              <button
                type="button"
                onClick={() => setShowStructure((v) => !v)}
                className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text transition-colors"
              >
                <span className="text-[9px] leading-none">
                  {showStructure ? "▾" : "▸"}
                </span>
                Current site structure
                <span className="text-text-subtle">({targets.length})</span>
              </button>
            ) : (
              <span />
            )}
            <Button variant="primary" onClick={() => setStep("redirects")}>
              Next →
            </Button>
          </div>
        </div>
      ) : null}

      {step === "redirects" && (
        <div className="space-y-4">
          {/* Build + verification gate */}
          <div className="bg-bg-card border border-border rounded-lg p-4 shadow-card space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="primary"
                onClick={handleBuildRedirects}
                disabled={building || urls.length === 0 || targets.length === 0}
              >
                {building ? "Matching… (reading blog titles)" : "Rebuild Map"}
              </Button>
              {redirects.length > 0 && (
                <span className="text-xs text-text-muted">
                  {redirects.filter((r) => r.targetUrl && r.status !== "skipped").length}{" "}
                  matched ·{" "}
                  {redirects.filter((r) => !r.targetUrl && r.status !== "skipped").length}{" "}
                  need a target ·{" "}
                  {redirects.filter((r) => r.status === "skipped").length} 404
                </span>
              )}
            </div>

            {/* fix685: prominent "building" banner. The map often auto-builds on
                first visit, so without this a new user (e.g. a web-team member)
                only sees a greyed button and assumes nothing is happening. This
                makes the work obvious, with a live timer. */}
            {building && (
              <div className="flex items-center gap-3 rounded-md border border-accent bg-accent-subtle px-4 py-3">
                <span
                  className="inline-block h-4 w-4 shrink-0 rounded-full border-2 border-accent border-t-transparent animate-spin"
                  aria-hidden="true"
                />
                <div className="text-sm text-accent">
                  <span className="font-medium">Building redirect map…</span>{" "}
                  <span className="text-text-muted">
                    reading blog titles and matching old URLs to current pages.
                    This usually takes a few seconds
                    {buildElapsed > 0 ? ` — ${buildElapsed}s` : ""}.
                  </span>
                </div>
              </div>
            )}

            {/* chunk3z-fix402: multi-location prompt. Never auto-assign a
                location an old URL didn't claim — ask once, then apply. */}
            {locations?.isNamespaced && (
              <div className="rounded-md border border-accent/30 bg-accent-subtle px-3 py-2.5 space-y-2">
                <p className="text-sm text-text">
                  This site has multiple locations:{" "}
                  <span className="font-medium">
                    {locations.currentLocations.join(", ")}
                  </span>
                </p>
                {locations.genericPaths.length > 0 && (
                  <>
                    <p className="text-xs text-text-muted">
                      {locations.genericPaths.length} old page
                      {locations.genericPaths.length === 1 ? "" : "s"} don't
                      name a location. Which location should they redirect to?
                      Pick the one that needs the most SEO help.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={defaultLocation}
                        onChange={(e) => setDefaultLocation(e.target.value)}
                        className="bg-bg-input border border-border rounded-md px-2 py-1 text-sm text-text"
                      >
                        <option value="">— choose a location —</option>
                        {locations.currentLocations.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="primary"
                        onClick={handleBuildRedirects}
                        disabled={!defaultLocation || building}
                      >
                        Apply
                      </Button>
                    </div>
                  </>
                )}
                {locations.closedLocations.length > 0 && (
                  <p className="text-xs text-warning">
                    Closed location
                    {locations.closedLocations.length === 1 ? "" : "s"}:{" "}
                    <span className="font-medium">
                      {locations.closedLocations.join(", ")}
                    </span>{" "}
                    — present in the old scan but not on the current site. These
                    pages need a manual decision; they're never reassigned
                    automatically.
                  </p>
                )}
              </div>
            )}

            {/* Verification gate — the download stays locked until clean. */}
            {verify && (
              <div
                className={`rounded-md px-3 py-2 text-xs ${
                  verify.ok
                    ? "bg-success-bg text-success"
                    : "bg-warning-bg text-warning"
                }`}
              >
                {verify.ok ? (
                  <>
                    ✓ {verify.rows} rows · 0 duplicates · 0 invalid targets · 0
                    live-URL sources
                  </>
                ) : (
                  <>⚠ {verify.problems.join(" · ")}</>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <a
                href={verify?.ok ? getRedirectCsvUrl(client.id) : undefined}
                onClick={(e) => {
                  if (!verify?.ok) e.preventDefault();
                }}
                className={`text-sm rounded-md px-3 py-1.5 border transition-colors ${
                  verify?.ok
                    ? "bg-accent text-white border-accent hover:opacity-90"
                    : "border-border text-text-subtle cursor-not-allowed opacity-50"
                }`}
              >
                ⬇ Download redirects CSV
              </a>
              {!verify?.ok && verify && (
                <span className="text-xs text-text-muted">
                  Fix the issues above to unlock the download.
                </span>
              )}
            </div>
          </div>

          {/* Redirect table */}
          {redirects.length > 0 && (
            <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                <h2 className="text-text font-semibold text-sm">Redirect map</h2>
                <select
                  value={redirFilter}
                  onChange={(e) =>
                    setRedirFilter(e.target.value as typeof redirFilter)
                  }
                  className="bg-bg-input border border-border rounded-md px-2 py-1 text-xs text-text"
                >
                  <option value="all">All rows</option>
                  <option value="review">Needs checking</option>
                  <option value="unmatched">No target</option>
                </select>
                <span className="ml-auto text-xs text-text-muted">
                  {redirects.filter((r) => r.needsReview).length > 0 && (
                    <span className="text-warning font-medium mr-2">
                      {redirects.filter((r) => r.needsReview).length} need
                      checking
                    </span>
                  )}
                  {redirects.length} rows
                </span>
              </div>
              <div className="max-h-[26rem] overflow-auto">
                <table className="w-full text-sm table-fixed">
                  <thead className="sticky top-0 z-10 bg-bg-card shadow-[0_1px_0_var(--border)]">
                    <tr className="text-left text-xs text-text-muted">
                      <th className="px-4 py-2 font-medium w-[38%]">Source</th>
                      <th className="px-3 py-2 font-medium">Target</th>
                      <th className="px-3 py-2 font-medium w-[22%]">Why</th>
                      <th className="px-3 py-2 font-medium text-right w-28">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRedirects
                      .filter((r) =>
                        redirFilter === "unmatched"
                          ? !r.targetUrl && r.status !== "skipped"
                          : redirFilter === "review"
                            ? r.needsReview
                            : true
                      )
                      .map((r) => (
                        <tr
                          key={r.id}
                          className={`border-t border-border-subtle ${
                            r.needsReview
                              ? "bg-warning-bg"
                              : "odd:bg-bg-subtle/40"
                          } ${r.status === "skipped" ? "opacity-50" : ""}`}
                        >
                          <td className="px-4 py-2 text-text font-mono text-xs">
                            {/* chunk3z-fix541: single-line + ellipsis (was
                                break-all, which wrapped long URLs and made rows
                                taller). Full path on hover. */}
                            <div className="flex items-center truncate" title={r.sourcePath}>
                              {r.needsReview && (
                                <span
                                  className="mr-1.5 text-warning shrink-0"
                                  title="Auto-guessed — please check"
                                >
                                  ⚠
                                </span>
                              )}
                              <span className="truncate">{r.sourcePath}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5">
                            <select
                              value={r.targetUrl ?? ""}
                              onChange={(e) =>
                                setRedirectTarget(r, e.target.value)
                              }
                              className="w-full bg-transparent border border-transparent hover:border-border focus:border-accent rounded px-1 py-0.5 text-xs text-accent font-mono cursor-pointer"
                            >
                              <option value="">— no target (404) —</option>
                              {CATEGORY_ORDER.map((cat) =>
                                groupedTargets[cat].length > 0 ? (
                                  <optgroup
                                    key={cat}
                                    label={CATEGORY_LABELS[cat]}
                                  >
                                    {groupedTargets[cat].map((t) => (
                                      <option key={t.id} value={t.url}>
                                        {t.path}
                                      </option>
                                    ))}
                                  </optgroup>
                                ) : null
                              )}
                            </select>
                          </td>
                          <td className="px-3 py-1.5 text-xs text-text-subtle">
                            {/* chunk3z-fix541: keep the reason on ONE line
                                (truncate + ellipsis, full text on hover) so
                                every row is the same height. Variable-height
                                rows moved the OK button to a different vertical
                                spot each time, making rapid confirm-clicking
                                miss. table-fixed + truncate locks the rhythm. */}
                            <div className="truncate" title={r.matchReason ?? undefined}>
                              {r.matchReason}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <div className="flex items-center justify-end gap-3">
                              {r.needsReview && (
                                <button
                                  type="button"
                                  onClick={() => markRedirectOk(r)}
                                  className="text-xs font-medium text-success hover:underline"
                                  title="This target is correct — clear the warning"
                                >
                                  OK
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => skipRedirect(r)}
                                className="text-xs text-text-muted hover:text-danger"
                                title="Send this URL to a 404 — keeps the row as a deliberate skip"
                              >
                                404
                              </button>
                              {/* chunk3z-fix461: remove the row entirely — gone
                                  from the map and the CSV. Separated from 404 by
                                  a divider so the two aren't confused. */}
                              <span className="text-border-subtle select-none">
                                |
                              </span>
                              <button
                                type="button"
                                onClick={() => removeRedirect(r)}
                                className="text-sm leading-none text-text-subtle hover:text-danger"
                                title="Remove this row from the map — won't appear in the CSV"
                                aria-label="Remove row"
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Missing pages report — Gen 2 only, copy-paste */}
          <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <h2 className="text-text font-semibold text-sm">
                Missing pages report
              </h2>
              <span className="text-xs text-text-muted">
                Gen 2 only
                {reportCounts &&
                  ` · ${reportCounts.servicePages} service · ${reportCounts.blogPosts} blog · ${reportCounts.other} other · ${reportCounts.notHundred} not 100%`}
              </span>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(report);
                  toast.show("Report copied", { kind: "success" });
                }}
                disabled={!report}
                className="ml-auto text-xs rounded-md border border-border px-2.5 py-1 text-text-muted hover:text-accent hover:border-accent disabled:opacity-40"
              >
                Copy
              </button>
            </div>
            <pre className="p-4 text-sm text-text whitespace-pre-wrap font-sans max-h-[24rem] overflow-auto">
              {report || "Build the redirect map to generate the report."}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  muted,
}: {
  label: string;
  value: number;
  muted?: boolean;
}) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span
        className={`font-semibold ${muted ? "text-text-muted" : "text-text"}`}
      >
        {value.toLocaleString()}
      </span>
      <span className="text-xs text-text-muted">{label}</span>
    </span>
  );
}

/**
 * chunk3z-fix405: clickable column header. Shows a caret only on the active
 * column so the current sort is unambiguous; clicking the active column flips
 * direction, clicking another switches to it.
 */
function SortHeader({
  label,
  col,
  sortKey,
  sortAsc,
  onSort,
  className = "",
}: {
  label: string;
  col: "path" | "firstCapture" | "lastCapture" | "generation" | "source";
  sortKey: string;
  sortAsc: boolean;
  onSort: (
    c: "path" | "firstCapture" | "lastCapture" | "generation" | "source"
  ) => void;
  className?: string;
}) {
  const active = sortKey === col;
  return (
    <th className={`font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 hover:text-accent transition-colors ${
          active ? "text-accent" : ""
        }`}
        title={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        <span className="text-[9px] leading-none">
          {active ? (sortAsc ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

function GenBadge({
  gen,
  prelaunch,
}: {
  gen: "gen1" | "gen2" | "gen3";
  prelaunch?: boolean;
}) {
  // chunk3z-fix400: consistent colour language everywhere a generation is
  // shown — Gen 1 green, Gen 2 yellow, Gen 3+ light orange. Previously Gen 3+
  // was grey, which read as "disabled" rather than "older site".
  const map = {
    gen1: { label: "Gen 1", cls: "bg-success-bg text-success" },
    gen2: { label: "Gen 2", cls: "bg-warning-bg text-warning" },
    gen3: { label: "Gen 3+", cls: "bg-gen3-bg text-gen3" },
  } as const;
  const { label, cls } = map[gen];
  // Pre-launch clients have no "ours" yet — gen1 is simply their live site.
  const text = prelaunch && gen === "gen1" ? "Current" : label;
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${cls}`}
    >
      {text}
    </span>
  );
}

/**
 * chunk3z-fix434: where an old URL was recovered from. "Both" is the best case
 * (Archive + GSC agree, deduplicated to one row); "GSC" flags a page the Wayback
 * scan missed entirely — the recovery the Search Console connection exists for.
 */
function SourceBadge({
  source,
  gscClicks,
}: {
  source: string;
  gscClicks?: number | null;
}) {
  const map: Record<string, { label: string; cls: string; title: string }> = {
    wayback: {
      label: "Archive",
      cls: "bg-outline-bg text-outline",
      title: "Found only in the Wayback Machine archive scan",
    },
    gsc: {
      label: "GSC",
      cls: "bg-accent-subtle text-accent",
      title: "Found only in Google Search Console — Wayback missed this page",
    },
    both: {
      label: "Both",
      cls: "bg-success-bg text-success",
      title: "Found in Archive and GSC — deduplicated into one row",
    },
  };
  const m = map[source] ?? {
    label: source || "—",
    cls: "bg-bg-subtle text-text-muted",
    title: source,
  };
  // chunk3z-fix446: a page with clicks earned real traffic — the highest-value
  // redirects. Most GSC rows have impressions but zero clicks, so the ones WITH
  // clicks are worth flagging at a glance: a dot on the badge, and a short
  // clicks-only tooltip (the long provenance sentence was overwhelming).
  const hasClicks = gscClicks != null && gscClicks > 0;
  const title = hasClicks
    ? `${gscClicks} click${gscClicks === 1 ? "" : "s"} (last 16 months)`
    : m.title;
  return (
    <span
      title={title}
      className={`relative inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[11px] font-medium w-14 ${m.cls}`}
    >
      {m.label}
      {/* chunk3z-fix481: the clicks dot is absolutely positioned so it never
          changes the badge width — every badge is the same size (w-14) and they
          line up as a clean column. */}
      {hasClicks && (
        <span
          className="absolute right-1 h-1.5 w-1.5 rounded-full bg-current"
          aria-label={`${gscClicks} clicks`}
        />
      )}
    </span>
  );
}
interface TreeNode {
  segment: string;
  path: string;
  children: Map<string, TreeNode>;
  isPage: boolean;
}

function buildTree(paths: string[]): TreeNode {
  const root: TreeNode = {
    segment: "/",
    path: "/",
    children: new Map(),
    isPage: false,
  };
  for (const p of paths) {
    const parts = p.split("/").filter(Boolean);
    let node = root;
    let acc = "";
    if (parts.length === 0) {
      root.isPage = true;
      continue;
    }
    for (const part of parts) {
      acc += `/${part}`;
      let child = node.children.get(part);
      if (!child) {
        child = { segment: part, path: acc + "/", children: new Map(), isPage: false };
        node.children.set(part, child);
      }
      node = child;
    }
    node.isPage = true;
  }
  return root;
}

function SitemapTree({ targets }: { targets: MigrationTarget[] }) {
  // chunk3z-fix420: the search box is gone — the list is already split into
  // Pages and Blog posts, which is the structure this panel exists to show.
  // chunk3z-fix419: split regular pages from blog posts. Many sites publish
  // posts at the ROOT (/post-name/) rather than nested under /blog/, so a plain
  // path tree scatters dozens of posts through the top level and buries the
  // real page structure. sitemapType comes from which sitemap the URL was found
  // in (page- vs post-sitemap.xml), so it identifies posts regardless of URL
  // shape; the path check is a fallback for sitemaps that don't say.
  const { pages, posts, blogHome } = useMemo(() => {
    const isPost = (t: MigrationTarget) =>
      t.sitemapType === "post" ||
      t.sitemapType === "posts" ||
      /^\/(blogs?|news|articles)\/.+/i.test(t.path);
    const home = targets.find((t) => {
      const p = t.path.toLowerCase().replace(/\/+$/, "");
      return p === "/blog" || p === "/blogs" || p === "/news" || p === "/articles";
    });
    return {
      pages: targets.filter((t) => !isPost(t) && t !== home),
      posts: targets.filter(isPost),
      blogHome: home ?? null,
    };
  }, [targets]);

  const pageTree = useMemo(() => buildTree(pages.map((t) => t.path)), [pages]);

  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1">
        Pages <span className="font-normal normal-case">({pages.length})</span>
      </div>
      <ul className="text-sm">
        {[...pageTree.children.values()].map((child) => (
          <TreeBranch key={child.path} node={child} depth={0} />
        ))}
      </ul>

      {(posts.length > 0 || blogHome) && (
        <>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mt-4 mb-1 pt-3 border-t border-border-subtle">
            Blog posts{" "}
            <span className="font-normal normal-case">({posts.length})</span>
          </div>
          {blogHome && (
            <div className="text-sm text-text mb-1">
              {blogHome.path}{" "}
              <span className="text-[11px] text-text-subtle">(blog home)</span>
            </div>
          )}
          <ul className="text-sm">
            {posts.map((t) => (
              <li
                key={t.id}
                className="text-text-muted py-0.5"
                style={{ paddingLeft: blogHome ? 14 : 0 }}
              >
                {t.path}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// chunk3z-fix477: render the FOUND pages (old URLs) as the same parent/child
// tree the current-site-structure panel uses. For a not-launched prospect the
// found pages ARE their site, so a flat list buried the structure; the tree
// shows top-level pages with their children nested.
// chunk3z-fix479: tree node that also carries the MigrationUrl at its leaf, so
// Pages found can show the source badge and a per-item remove control.
interface UrlTreeNode {
  segment: string;
  path: string;
  children: Map<string, UrlTreeNode>;
  url: MigrationUrl | null;
}

// chunk3z-fix488: gather every MigrationUrl at or under a node — used to remove
// a structure-only parent and all its descendants in one click.
function collectUrls(node: UrlTreeNode): MigrationUrl[] {
  const out: MigrationUrl[] = [];
  if (node.url) out.push(node.url);
  for (const child of node.children.values()) out.push(...collectUrls(child));
  return out;
}

function buildUrlTree(
  urls: MigrationUrl[],
  displayPath?: (u: MigrationUrl) => string
): UrlTreeNode {
  const root: UrlTreeNode = {
    segment: "/",
    path: "/",
    children: new Map(),
    url: null,
  };
  for (const u of urls) {
    const p = displayPath ? displayPath(u) : u.path;
    const parts = p.split("/").filter(Boolean);
    let node = root;
    let acc = "";
    if (parts.length === 0) {
      root.url = u;
      continue;
    }
    for (const part of parts) {
      acc += `/${part}`;
      let child = node.children.get(part);
      if (!child) {
        child = { segment: part, path: acc + "/", children: new Map(), url: null };
        node.children.set(part, child);
      }
      node = child;
    }
    node.url = u;
  }
  // chunk3z-fix495: prune "featured image" children — a child whose slug is the
  // SAME as (or contained in) its parent's slug is the post's featured image
  // uploaded as an attachment page (/dental-health-tips-for-traveling/dental-
  // health-tips-for-traveling/). Drop it from the tree; it's an image, not a page.
  pruneFeaturedImages(root);
  // chunk3z-fix498: order every level alphabetically by segment. Map preserves
  // INSERTION order (scan order), so without this a moved item lands wherever it
  // happened to be added. Sorting means a page moved "→ to services" / "→ to
  // blog" pops into its correct alphabetical slot within the section.
  sortTree(root);
  return root;
}

function sortTree(node: UrlTreeNode): void {
  const sorted = [...node.children.entries()].sort((a, b) =>
    a[0].localeCompare(b[0], undefined, { numeric: true })
  );
  node.children = new Map(sorted);
  for (const child of node.children.values()) sortTree(child);
}

function slugStem(seg: string): string {
  return seg.toLowerCase().replace(/-\d+$/, ""); // drop a trailing -2 dedupe
}

function pruneFeaturedImages(node: UrlTreeNode): void {
  const parentStem = slugStem(node.segment);
  for (const [key, child] of [...node.children]) {
    const childStem = slugStem(child.segment);
    // Only prune LEAF children (no grandchildren) that are a real found page.
    const isLeaf = child.children.size === 0 && child.url != null;
    if (
      isLeaf &&
      parentStem.length >= 6 &&
      childStem === parentStem &&
      node.url != null // parent is itself a real page (the post)
    ) {
      node.children.delete(key);
      continue;
    }
    pruneFeaturedImages(child);
  }
}

// chunk3z-fix486: decide whether a found URL is a BLOG POST vs a common site
// page, so Pages found can group common pages on top and posts at the bottom.
// Mirrors the backend heuristics: under a blog/news/articles parent, a
// date-permalink, a question/listicle opener, or a long multi-word slug (long
// titles are almost always posts). Short slugs and known site sections are pages.
// chunk3z-fix491: find the site's SERVICES hub segment among the found URLs, so
// pages moved "→ to services" can be nested under it. Recognizes the common
// names: /services/, /dental-services/, /treatments/, /dental-treatments/, etc.
const SERVICE_PARENT_RE =
  /^(services?|dental-services?|our-services?|treatments?|dental-treatments?|procedures?|dental-procedures?)$/i;

// chunk3z-fix509: return a new Set with id removed, or the same Set if absent
// (so React bails out of a needless re-render). Used to keep the three "forced
// category" sets mutually exclusive — moving a page into one category clears it
// from the others.
// chunk3z-fix514: canonical key for comparing paths across sources. The crawler
// stores current-site targets without a trailing slash ("/foo") while wayback/
// GSC store urls with one ("/foo/"). Collapse both to one comparable value so a
// page whose sources disagree on the slash still matches. Mirrors the backend
// pathKey helper.
function pathKey(path: string): string {
  let p = (path || "").toLowerCase();
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p === "" ? "/" : p;
}

function dropFrom(set: Set<number>, id: number): Set<number> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

function findServicesParent(urls: MigrationUrl[]): string {
  const counts = new Map<string, number>();
  for (const u of urls) {
    const first = u.path.split("/").filter(Boolean)[0];
    if (first && SERVICE_PARENT_RE.test(first)) {
      counts.set(first, (counts.get(first) ?? 0) + 1);
    }
  }
  // Most-used matching segment wins; default to "services" if none exists yet.
  let best = "services";
  let bestN = -1;
  for (const [seg, n] of counts) {
    if (n > bestN) {
      best = seg;
      bestN = n;
    }
  }
  return best;
}

function looksLikeBlogPost(path: string): boolean {
  const p = path.toLowerCase();
  if (/^\/(blogs?|news|articles?|posts?|stories)\//.test(p)) return true;
  if (/(^|\/|-)articles?(\/|$|-)/.test(p)) return true;
  if (/^\/\d{4}\/\d{1,2}(\/\d{1,2})?\//.test(p)) return true; // date permalink
  // chunk3z-fix513: a page NESTED under a service hub is a service page, never a
  // post — regardless of how sentence-like its slug reads. On this client
  // /services/all-on-x-full-arch-dental-implants (7 words) and
  // /services/cbct-cone-beam-ct-3d-imaging (6 words) were tripping the
  // "5+ words = post" heuristic below and landing in Blog posts. Any first
  // segment naming a services/treatments/procedures hub scopes everything under
  // it as pages. Mirrors the backend isBlogPath guard, which already exempts
  // service paths; this frontend copy had drifted without it.
  if (/^\/(services?|treatments?|procedures?|dental-services|our-services)\//.test(p)) {
    return false;
  }
  const segs = p.split("/").filter(Boolean);
  const slug = segs[segs.length - 1] ?? "";
  const words = slug.split("-").filter(Boolean);
  // Known common-page slugs are never posts.
  if (
    /^(home|home-\d+|about|about-us|contact|contact-us|services?|team|our-team|meet-the-team|meet-the-doctors?|reviews|testimonials|gallery|smile-gallery|new-patients?|patient-info|financial|financing|insurance|appointments?|book|blog|location|locations|areas-we-serve|faq|faqs|privacy-policy|accessibility|sitemap)$/.test(
      slug
    )
  ) {
    return false;
  }
  // A question/listicle opener is a post.
  if (
    /^(how|why|what|when|where|which|can|should|is|are|do|does|will|top|best|signs?|tips?|reasons?|ways?|guide|understanding|benefits?|\d+)-/.test(
      slug
    )
  ) {
    return true;
  }
  // Long multi-word titles are almost always posts (user's rule).
  return words.length >= 5;
}

function PagesFoundTree({
  urls,
  onRemove,
  onMoveToBlog,
  onMoveToService,
  onMoveToPage,
  onMoveSubtreeToPage,
  forcedBlog,
  forcedService,
  forcedPage,
}: {
  urls: MigrationUrl[];
  onRemove: (u: MigrationUrl) => void;
  onMoveToBlog?: (u: MigrationUrl) => void;
  onMoveToService?: (u: MigrationUrl) => void;
  onMoveToPage?: (u: MigrationUrl) => void;
  onMoveSubtreeToPage?: (node: UrlTreeNode) => void;
  forcedBlog?: Set<number>;
  forcedService?: Set<number>;
  forcedPage?: Set<number>;
}) {
  // chunk3z-fix486/491: split into common PAGES (top) and BLOG POSTS (bottom).
  // A URL manually moved to blog (forcedBlog) always sorts into Blog posts. A URL
  // moved "→ to services" (forcedService) is reparented under the site's services
  // hub so it nests there visually.
  const servicesSeg = useMemo(() => findServicesParent(urls), [urls]);

  const reparent = useMemo(() => {
    return (u: MigrationUrl): string => {
      if (forcedService?.has(u.id)) {
        const slug = u.path.split("/").filter(Boolean).pop() ?? "";
        return `/${servicesSeg}/${slug}/`;
      }
      return u.path;
    };
  }, [forcedService, servicesSeg]);

  const { pageUrls, postUrls } = useMemo(() => {
    const pageUrls: MigrationUrl[] = [];
    const postUrls: MigrationUrl[] = [];
    for (const u of urls) {
      // A page forced to services is always a PAGE (never a post), even if its
      // title looks post-like.
      const forcedSvc = forcedService?.has(u.id);
      // chunk3z-fix509: forcedPage pulls a mis-sorted item into Pages, beating
      // both the looksLikeBlogPost heuristic and a forcedBlog flag we cleared.
      const forcedPg = forcedPage?.has(u.id);
      const isPost =
        !forcedSvc &&
        !forcedPg &&
        (forcedBlog?.has(u.id) || looksLikeBlogPost(u.path));
      (isPost ? postUrls : pageUrls).push(u);
    }
    return { pageUrls, postUrls };
  }, [urls, forcedBlog, forcedService, forcedPage]);

  const pageTree = useMemo(
    () => buildUrlTree(pageUrls, reparent),
    [pageUrls, reparent]
  );
  const postTree = useMemo(() => buildUrlTree(postUrls), [postUrls]);
  const pageTop = [...pageTree.children.values()];
  // chunk3z-fix525: always float the blog-home node (/blog, /news, /articles…)
  // to the TOP of the Blog posts section. It's the parent of the whole section,
  // so it reads oddly buried alphabetically among the individual posts. Detect it
  // by segment and sort it first; everything else keeps its order.
  const isBlogHomeSeg = (seg: string) =>
    /^(blogs?|news|articles?|insights|posts?)$/i.test(seg.replace(/^\/+/, ""));
  const postTop = [...postTree.children.values()].sort((a, b) => {
    const aHome = isBlogHomeSeg(a.segment);
    const bHome = isBlogHomeSeg(b.segment);
    if (aHome && !bHome) return -1;
    if (bHome && !aHome) return 1;
    return 0;
  });

  if (pageTop.length === 0 && postTop.length === 0) {
    return (
      <div className="p-6 text-center text-text-muted text-sm">
        No pages found yet — run Scan History and/or Crawl site.
      </div>
    );
  }
  return (
    <div className="text-sm">
      {pageTop.length > 0 && (
        <>
          <div className="sticky top-0 z-10 bg-bg-card text-[11px] font-semibold uppercase tracking-wide text-text-muted py-1.5 -mx-4 px-4 border-b border-border-subtle shadow-[0_1px_0_var(--border-subtle)]">
            Pages{" "}
            <span className="font-normal normal-case">({pageUrls.length})</span>
          </div>
          <ul>
            {pageTop.map((child) => (
              <UrlTreeBranch
                key={child.path}
                node={child}
                depth={0}
                onRemove={onRemove}
                onMoveToBlog={onMoveToBlog}
                onMoveToService={onMoveToService}
                forcedService={forcedService}
              />
            ))}
          </ul>
        </>
      )}
      {postTop.length > 0 && (
        <>
          <div className="sticky top-0 z-10 bg-bg-card text-[11px] font-semibold uppercase tracking-wide text-text-muted py-1.5 mt-2 -mx-4 px-4 border-y border-border-subtle shadow-[0_1px_0_var(--border-subtle)]">
            Blog posts{" "}
            <span className="font-normal normal-case">({postUrls.length})</span>
          </div>
          <ul>
            {postTop.map((child) => (
              <UrlTreeBranch
                key={child.path}
                node={child}
                depth={0}
                onRemove={onRemove}
                onMoveToPage={onMoveToPage}
                onMoveSubtreeToPage={onMoveSubtreeToPage}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function UrlTreeBranch({
  node,
  depth,
  onRemove,
  onMoveToBlog,
  onMoveToService,
  onMoveToPage,
  onMoveSubtreeToPage,
  forcedService,
}: {
  node: UrlTreeNode;
  depth: number;
  onRemove: (u: MigrationUrl) => void;
  onMoveToBlog?: (u: MigrationUrl) => void;
  onMoveToService?: (u: MigrationUrl) => void;
  onMoveToPage?: (u: MigrationUrl) => void;
  // chunk3z-fix539: move a PARENT and ALL its descendants to Pages at once.
  // Used for a mis-sorted section like /contact-us that landed in Blog with its
  // children nested under it.
  onMoveSubtreeToPage?: (node: UrlTreeNode) => void;
  forcedService?: Set<number>;
}) {
  // chunk3z-fix479: auto-expand ALL levels so children are visible without
  // clicking. The user can still collapse any branch.
  const [open, setOpen] = useState(true);
  const kids = [...node.children.values()];
  const hasKids = kids.length > 0;
  // chunk3z-fix524: dim a junk leaf so it recedes from the real pages around it.
  const isJunkRow = node.url?.isJunk ?? false;
  return (
    <li>
      <div
        className={`group flex items-center gap-1.5 py-0.5 pr-2 rounded hover:bg-bg-subtle ${
          isJunkRow ? "opacity-60" : ""
        }`}
      >
        {/* chunk3z-fix488: remove (✕) on EVERY row, including structure-only
            parents. A parent that isn't itself a found page (just a path segment)
            still needs a remove — removing it drops all its descendant URLs at
            once (e.g. a junk /welcome-.../ parent holding two ad images). */}
        <button
          type="button"
          onClick={() => {
            if (node.url) onRemove(node.url);
            else collectUrls(node).forEach(onRemove);
          }}
          className="w-4 shrink-0 text-sm leading-none text-text-subtle opacity-0 group-hover:opacity-100 hover:text-danger transition-opacity"
          title={
            node.url
              ? "Remove this item — not a real page"
              : "Remove this group and everything under it"
          }
          aria-label="Remove item"
        >
          ✕
        </button>
        {/* Indentation + disclosure + name live in a flex-1 middle region so the
            source badge can right-align in its own column across all rows. */}
        <div
          className="flex items-center gap-1.5 min-w-0 flex-1"
          style={{ paddingLeft: depth * 24 }}
        >
          {hasKids ? (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="text-text-subtle text-xs w-3 shrink-0"
            >
              {open ? "▾" : "▸"}
            </button>
          ) : (
            <span className="w-3 shrink-0" />
          )}
          {node.url ? (
            // chunk3z-fix482: a real page node links to its LIVE url in a new
            // tab, so you can spot-check whether it's a genuine page. An external
            // arrow appears on hover to signal it's clickable.
            // chunk3z-fix488: parents (nodes with children) are BOLD so the site
            // structure stands out — still clickable links.
            <a
              href={node.url.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`truncate hover:text-accent hover:underline inline-flex items-center gap-1 ${
                hasKids ? "font-semibold text-text" : "text-text"
              }`}
              title={`Open ${node.url.url} in a new tab`}
              onClick={(e) => e.stopPropagation()}
            >
              /{node.segment}
              <span className="text-[10px] text-text-subtle opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                ↗
              </span>
            </a>
          ) : (
            <span
              className={`truncate ${
                hasKids ? "font-semibold text-text" : "text-text-muted"
              }`}
            >
              /{node.segment}
            </span>
          )}
          {hasKids && (
            <span className="text-[11px] text-text-subtle shrink-0">
              ({kids.length})
            </span>
          )}
          {/* chunk3z-fix524: when "Show junk" reveals filtered rows, mark them
              clearly. A junk row gets a red "JUNK" pill with its reason on hover,
              so you can tell at a glance which items in the list were filtered out
              (and why) versus the real pages around them. */}
          {node.url?.isJunk && (
            <span
              className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-danger-bg text-danger border border-danger-border shrink-0"
              title={
                node.url.junkReason
                  ? `Filtered as junk: ${node.url.junkReason}. Not included in the redirect map. Click the checkbox / ✕ to keep it.`
                  : "Filtered as junk — not included in the redirect map."
              }
            >
              junk{node.url.junkReason ? ` · ${node.url.junkReason}` : ""}
            </span>
          )}
          {/* chunk3z-fix497: a page moved "→ to services" is shown NESTED under
              the services hub for organization, but its real URL is unchanged.
              Make that explicit so no one thinks the URL became
              /services/<slug>. Show a subtle "moved · real URL" marker. */}
          {node.url && forcedService?.has(node.url.id) && (
            <span
              className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-text-subtle shrink-0"
              title={`Moved here for organization only. Real URL: ${node.url.path}`}
            >
              <span className="rounded bg-bg-subtle border border-border-subtle px-1 py-0.5 leading-none">
                moved · real URL {node.url.path}
              </span>
            </span>
          )}
          {/* chunk3z-fix488/491: move a mis-sorted page to Blog posts, or a
              service page under the services hub. Shown for page nodes on hover
              in the Pages group; each pushes the item in real time. */}
          {node.url && onMoveToBlog && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveToBlog(node.url!);
              }}
              className="ml-1 text-[10px] text-text-subtle opacity-0 group-hover:opacity-100 transition-opacity hover:text-accent hover:underline shrink-0"
              title="Move to Blog posts"
            >
              → to blog
            </button>
          )}
          {node.url && onMoveToService && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveToService(node.url!);
              }}
              className="ml-1 text-[10px] text-text-subtle opacity-0 group-hover:opacity-100 transition-opacity hover:text-accent hover:underline shrink-0"
              title="Move under the services hub"
            >
              → to services
            </button>
          )}
          {/* chunk3z-fix509: pull a mis-sorted blog post back into Pages. Shown
              on post rows (where onMoveToPage is supplied); mirrors "→ to blog"
              in the other direction. Lands the page at the Pages root, not under
              /services/ — for things like /why-choose-us that are plain pages a
              post-type sitemap mislabeled. */}
          {node.url && onMoveToPage && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveToPage(node.url!);
              }}
              className="ml-1 text-[10px] text-text-subtle opacity-0 group-hover:opacity-100 transition-opacity hover:text-accent hover:underline shrink-0"
              title="Move to Pages"
            >
              → to pages
            </button>
          )}
          {/* chunk3z-fix539: on a PARENT node that has children (e.g. /contact-us
              with its nested pages), offer moving the whole subtree to Pages in
              one click. This covers the case a section mis-sorted into Blog: the
              single-row "→ to pages" only moves the parent, leaving children
              behind, so a parent with kids gets this subtree action instead of
              (or in addition to) the single move. */}
          {node.children.size > 0 && onMoveSubtreeToPage && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveSubtreeToPage(node);
              }}
              className="ml-1 text-[10px] text-text-subtle opacity-0 group-hover:opacity-100 transition-opacity hover:text-accent hover:underline shrink-0"
              title="Move this page and all pages nested under it to Pages"
            >
              → to pages (with {node.children.size} nested)
            </button>
          )}
        </div>
        {/* Source column — fixed position on the right so every badge lines up. */}
        <div className="w-16 shrink-0 flex justify-end">
          {node.url && (
            <SourceBadge
              source={node.url.source}
              gscClicks={node.url.gscClicks}
            />
          )}
        </div>
      </div>
      {open && hasKids && (
        <ul>
          {kids.map((k) => (
            <UrlTreeBranch
              key={k.path}
              node={k}
              depth={depth + 1}
              onRemove={onRemove}
              onMoveToBlog={onMoveToBlog}
              onMoveToService={onMoveToService}
              onMoveToPage={onMoveToPage}
              onMoveSubtreeToPage={onMoveSubtreeToPage}
              forcedService={forcedService}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function TreeBranch({ node, depth }: { node: TreeNode; depth: number }) {
  const [open, setOpen] = useState(depth < 1);
  const kids = [...node.children.values()];
  const hasKids = kids.length > 0;
  return (
    <li>
      <div
        className="flex items-center gap-1.5 py-0.5"
        style={{ paddingLeft: depth * 24 }}
      >
        {hasKids ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-text-subtle text-xs w-3"
          >
            {open ? "▾" : "▸"}
          </button>
        ) : (
          <span className="w-3" />
        )}
        <span className={node.isPage ? "text-text" : "text-text-muted"}>
          /{node.segment}
        </span>
        {hasKids && (
          <span className="text-[11px] text-text-subtle">({kids.length})</span>
        )}
      </div>
      {open && hasKids && (
        <ul>
          {kids.map((k) => (
            <TreeBranch key={k.path} node={k} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * fix622: Cross-domain redirects. When a client moved from an OLD domain to the
 * current site (e.g. wellenerdental.com → pinehurst-dentist.com), this scans the
 * old domain's live sitemap + latest Wayback capture for source URLs, matches
 * them to the current site's pages, and exports an nginx redirect file.
 */
function CrossDomainRedirects({
  client,
  newDomain,
}: {
  client: Client;
  newDomain: string;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [oldDomain, setOldDomain] = useState("");
  const [scanning, setScanning] = useState(false);
  const [sources, setSources] = useState<OldSourceMatch[]>([]);
  const [targetOptions, setTargetOptions] = useState<{ url: string; path: string }[]>([]);
  const [loaded, setLoaded] = useState(false);
  // fix629: nginx format is fixed to "rewrite" (was a rewrite/location dropdown).

  // Load any previously-scanned old sources when the panel opens.
  useEffect(() => {
    if (!open || loaded) return;
    void (async () => {
      try {
        const r = await getOldSources(client.id);
        if (r.oldDomain) setOldDomain(r.oldDomain);
        setSources(r.sources);
      } catch {
        /* none yet */
      }
      try {
        const t = await getTargetOptions(client.id);
        setTargetOptions(t.targets);
      } catch {
        /* none */
      }
      setLoaded(true);
    })();
  }, [open, loaded, client.id]);

  async function refreshSources() {
    const r = await getOldSources(client.id);
    setSources(r.sources);
  }

  // fix655: skip / unskip a source — "no redirect needed" for legal pages etc.
  // A skipped row keeps no target, drops out of the needs-review count, and is
  // left out of the exported redirect file.
  async function toggleSkip(row: OldSourceMatch, skip: boolean) {
    if (row.id == null) return;
    setSources((cur) =>
      cur.map((s) =>
        s.id === row.id
          ? { ...s, skipped: skip, targetUrl: skip ? null : s.targetUrl, needsReview: false }
          : s
      )
    );
    try {
      await patchOldSource(row.id, { skipped: skip });
      await refreshSources();
    } catch {
      toast.show("Couldn't update", { kind: "error" });
      await refreshSources();
    }
  }

  async function changeTarget(row: OldSourceMatch, newTarget: string) {
    if (row.id == null) return;
    // fix727: "Custom URL…" sentinel — let the user type a target that isn't in
    // the sitemap (e.g. a noindexed legal page like /privacy-policy/ that RankMath
    // excludes from the XML but that exists on the site). Saved as an override.
    if (newTarget === "__custom__") {
      const entered = window.prompt(
        "Enter the target URL or path (e.g. /privacy-policy/ or https://site.com/privacy-policy/):",
        row.targetUrl ?? ""
      );
      if (entered == null) return; // cancelled
      newTarget = entered.trim();
      if (!newTarget) return;
    }
    // fix629: optimistically clear the needs-review highlight the moment a
    // target is chosen (backend also sets reviewed=TRUE alongside the override),
    // mirroring the Redirects tab.
    setSources((cur) =>
      cur.map((s) =>
        s.id === row.id
          ? { ...s, targetUrl: newTarget || null, overridden: !!newTarget, needsReview: false }
          : s
      )
    );
    try {
      await patchOldSource(row.id, { targetOverride: newTarget || null });
      await refreshSources();
    } catch {
      toast.show("Couldn't update target", { kind: "error" });
      await refreshSources();
    }
  }

  // fix629: "OK" — confirm an auto-guessed target as-is; persists reviewed=TRUE
  // so the ⚠ highlight stays cleared across refreshes.
  async function markReviewed(row: OldSourceMatch) {
    if (row.id == null) return;
    setSources((cur) =>
      cur.map((s) => (s.id === row.id ? { ...s, needsReview: false } : s))
    );
    try {
      await patchOldSource(row.id, { reviewed: true });
    } catch {
      toast.show("Couldn't confirm that row", { kind: "error" });
      await refreshSources();
    }
  }

  async function removeRow(row: OldSourceMatch) {
    if (row.id == null) return;
    // optimistic remove
    setSources((cur) => cur.filter((s) => s.id !== row.id));
    try {
      await patchOldSource(row.id, { removed: true });
    } catch {
      toast.show("Couldn't remove row", { kind: "error" });
      await refreshSources();
    }
  }

  async function handleScan() {
    const d = oldDomain.trim();
    if (!d) {
      toast.show("Enter the old domain first.", { kind: "error" });
      return;
    }
    setScanning(true);
    try {
      const res = await scanOldDomain(client.id, d);
      // fix648: show the source breakdown, and warn loudly if Wayback failed —
      // that's the difference between "this domain really has few pages" and
      // "the archive scan silently died and we only got the live sitemap."
      if (res.waybackError) {
        toast.show(
          `Wayback scan failed (${res.waybackError}). Only found ${res.usable} live-sitemap page${
            res.usable === 1 ? "" : "s"
          } — the old domain's archived pages were NOT included. Try Scan again (archive.org is often briefly slow).`,
          { kind: "error" }
        );
      } else {
        const wbNote =
          typeof res.waybackKept === "number" && typeof res.sitemapContributed === "number"
            ? ` — live sitemap: ${res.sitemapContributed}, Wayback: ${res.waybackKept} (of ${res.waybackCount} captured)`
            : "";
        toast.show(
          `Found ${res.usable} usable old page${res.usable === 1 ? "" : "s"} (${res.found} total)${wbNote}`,
          { kind: res.usable > 0 ? "success" : "info" }
        );
      }
      const r = await getOldSources(client.id);
      setSources(r.sources);
      try {
        const t = await getTargetOptions(client.id);
        setTargetOptions(t.targets);
      } catch {
        /* ignore */
      }
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Scan failed", {
        kind: "error",
      });
    } finally {
      setScanning(false);
    }
  }

  // fix655: skipped rows are intentionally not redirected — exclude them from
  // all three counts (they're neither matched, nor awaiting a target, nor
  // needing review). A separate skipped count is shown so they're still visible.
  const activeSources = sources.filter((s) => !s.skipped);
  const matched = activeSources.filter((s) => s.targetUrl).length;
  const unmatched = activeSources.length - matched;
  const needsReviewCount = activeSources.filter((s) => s.needsReview).length;
  const skippedCount = sources.filter((s) => s.skipped).length;

  // fix629: group the target dropdown into MAIN / BLOG POSTS / OTHER / UNKNOWN.
  const xdomGroups = useMemo(
    () => buildXDomGroups(targetOptions, xdomLocationPrefix(client.websiteUrl)),
    [targetOptions, client.websiteUrl]
  );
  // fix727: is a given target URL present among the sitemap-derived options? Used
  // to decide whether a saved custom override needs its own synthetic <option>.
  const targetUrlSet = useMemo(
    () => new Set(targetOptions.map((t) => t.url)),
    [targetOptions]
  );
  const targetInOptions = useCallback(
    (url: string): boolean => targetUrlSet.has(url),
    [targetUrlSet]
  );
  // fix651: for a multi-location client every target starts with the same
  // location prefix (/northeast-philadelphia/…), which is repeated noise in the
  // dropdown. Strip it from the DISPLAY LABEL only — the <option value> stays the
  // full path, so what's saved as the redirect target is unchanged. The bare
  // location path (the home page) shows as "/" so it doesn't collapse to empty.
  const xdomTargetPrefix = useMemo(
    () => xdomLocationPrefix(client.websiteUrl),
    [client.websiteUrl]
  );
  const xdomLabel = useCallback(
    (path: string): string => {
      if (!xdomTargetPrefix) return path;
      const stripped = stripLocationPrefix(path, xdomTargetPrefix);
      // stripLocationPrefix returns "" for the bare location (home) path; show
      // "/" so it's visible and not an empty option.
      return stripped === "" ? "/" : stripped;
    },
    [xdomTargetPrefix]
  );
  // fix652: the home page URL, for the one-click "send to home" button on each
  // row. It's the hub of the MAIN "home" slot (fix649 puts the location home —
  // /northeast-philadelphia/ — or the root / there). Null when no home target
  // exists yet (e.g. current site not loaded), which disables the button.
  const xdomHomeUrl = useMemo<string | null>(() => {
    const homeSlot = xdomGroups.main.find((g) => g.slot.key === "home");
    return homeSlot?.hub?.url ?? null;
  }, [xdomGroups]);

  return (
    <div className="border border-border rounded-lg bg-bg-card shadow-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-text">
          Moved from a different domain? Build cross-domain redirects
        </span>
        <span className="text-text-subtle text-lg">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-border-subtle pt-4 space-y-4">
          <p className="text-xs text-text-muted">
            For a client that changed domains (e.g. their old site was
            <code className="mx-1 px-1 bg-bg-hover/40 rounded">wellenerdental.com</code>
            and the new one is
            <code className="mx-1 px-1 bg-bg-hover/40 rounded">{newDomain || "the current site"}</code>).
            We scan the old domain's live sitemap + its latest Wayback capture for old
            URLs, match them to the current site, and export an nginx redirect file.
          </p>

          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-text text-sm font-medium mb-1">
                Old domain (moved from)
              </label>
              <input
                value={oldDomain}
                onChange={(e) => setOldDomain(e.target.value)}
                placeholder="wellenerdental.com"
                className="w-full bg-bg-input border border-border rounded-md px-3 py-1.5 text-sm text-text focus:outline-none focus:border-accent"
              />
            </div>
            <button
              type="button"
              onClick={() => void handleScan()}
              disabled={scanning || !oldDomain.trim()}
              className="bg-accent hover:bg-accent-dark text-white text-sm rounded-md px-4 py-2 disabled:opacity-50"
            >
              {scanning ? "Scanning old domain…" : "Scan old domain"}
            </button>
          </div>

          {scanning && (
            <p className="text-xs text-text-muted">
              Pulling the old domain's sitemap + Wayback history — can take a minute.
            </p>
          )}

          {sources.length > 0 && (
            <>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-text">
                  <strong>{sources.length}</strong> old pages
                </span>
                <span className="text-success">{matched} matched</span>
                {unmatched > 0 && (
                  <span className="text-warning">{unmatched} need a target</span>
                )}
                {needsReviewCount > 0 && (
                  <span className="text-warning font-medium">
                    {needsReviewCount} to review
                  </span>
                )}
                {skippedCount > 0 && (
                  <span className="text-neutral-400">{skippedCount} skipped</span>
                )}
              </div>

              {/* Match table */}
              <div className="border border-border rounded-md overflow-hidden max-h-96 overflow-y-auto">
                <table className="w-full text-xs border-collapse table-fixed">
                  <thead className="bg-neutral-100 sticky top-0 z-10">
                    <tr>
                      {/* fix630: fixed layout with explicit widths so the target
                          column gets a guaranteed wide share (was auto-layout,
                          which let long source paths hog the width and squeeze
                          the dropdowns). Source truncates; target fills. */}
                      <th className="text-left px-3 py-2 font-medium text-neutral-600 bg-neutral-100 w-[44%]">
                        Old page (source)
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-neutral-600 bg-neutral-100">
                        New page (target)
                      </th>
                      <th className="w-8 bg-neutral-100"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s) => (
                      <tr
                        key={s.id ?? s.sourcePath}
                        className={`border-t border-neutral-200 ${
                          s.needsReview ? "bg-warning-bg" : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-3 py-1.5 font-mono text-neutral-800 align-middle ${
                            s.needsReview ? "bg-warning-bg" : "bg-white"
                          }`}
                        >
                          <div className="flex items-center">
                            {s.needsReview && (
                              <span
                                className="mr-1.5 text-warning shrink-0"
                                title="Auto-guessed — please check"
                              >
                                ⚠
                              </span>
                            )}
                            <span className="truncate">{s.sourcePath}</span>
                          </div>
                        </td>
                        <td
                          className={`px-3 py-1.5 align-middle ${
                            s.needsReview ? "bg-warning-bg" : "bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            {s.skipped ? (
                              // fix655: skipped row — no redirect, no target picker.
                              <span className="w-full text-xs italic text-neutral-400 px-2 py-1 border border-dashed border-neutral-300 rounded">
                                Skipped — no redirect (won't be in the export)
                              </span>
                            ) : (
                              <>
                            {/* fix652: one-click "send to home page" — for old
                                pages with no equivalent, forward to home without
                                hunting the dropdown. Sets the same value the Home
                                option would. Disabled + dimmed when no home target
                                exists, or highlighted when this row already points
                                at home. */}
                            <button
                              type="button"
                              onClick={() =>
                                xdomHomeUrl && void changeTarget(s, xdomHomeUrl)
                              }
                              disabled={!xdomHomeUrl}
                              title={
                                xdomHomeUrl
                                  ? "Send this old page to the home page"
                                  : "No home page target found yet"
                              }
                              className={`shrink-0 flex items-center justify-center w-7 h-7 rounded border text-sm ${
                                s.targetUrl && s.targetUrl === xdomHomeUrl
                                  ? "border-accent text-accent bg-accent/5"
                                  : "border-neutral-300 text-neutral-500 hover:text-accent hover:border-accent"
                              } disabled:opacity-40 disabled:cursor-not-allowed`}
                              aria-label="Send to home page"
                            >
                              ⌂
                            </button>
                            <select
                              value={s.targetUrl ?? ""}
                              onChange={(e) => void changeTarget(s, e.target.value)}
                              className={`w-full border rounded px-2 py-1 font-mono text-xs bg-white ${
                                s.targetUrl
                                  ? s.overridden
                                    ? "border-accent text-accent"
                                    : "border-neutral-300 text-neutral-800"
                                  : "border-warning text-warning"
                              }`}
                          >
                            <option value="">— no match (manual) —</option>
                            <option value="__custom__">
                              ✏️ Custom URL… (not in sitemap)
                            </option>
                            {/* fix727: if the current target is a custom override
                                not present in the sitemap option groups, render it
                                so the select shows it instead of going blank. */}
                            {s.targetUrl &&
                              s.overridden &&
                              !targetInOptions(s.targetUrl) && (
                                <option value={s.targetUrl}>
                                  {s.targetUrl} (custom)
                                </option>
                              )}
                            {/* fix629: grouped in the site order Eddie knows. */}
                            {xdomGroups.main.length > 0 && (
                              <optgroup label={XDOM_GROUP_LABELS.main}>
                                {xdomGroups.main.map((slotGroup) => (
                                  <Fragment key={slotGroup.slot.key}>
                                    {slotGroup.hub && (
                                      <option
                                        key={slotGroup.hub.url}
                                        value={slotGroup.hub.url}
                                      >
                                        {xdomLabel(slotGroup.hub.path)}
                                      </option>
                                    )}
                                    {slotGroup.children.map((c) => (
                                      // fix650: indent children via CSS text-indent
                                      // instead of literal spaces in the label. A
                                      // native <select> shows the selected option's
                                      // full text (spaces included) in the collapsed
                                      // box, so nbsp-indented children rendered
                                      // shifted right when selected. text-indent
                                      // indents the option IN the open list but does
                                      // not carry into the collapsed value, so all
                                      // selected values left-align.
                                      <option
                                        key={c.url}
                                        value={c.url}
                                        style={{ textIndent: "1.5em" }}
                                      >
                                        {xdomLabel(c.path)}
                                      </option>
                                    ))}
                                  </Fragment>
                                ))}
                              </optgroup>
                            )}
                            {(xdomGroups.blogHome.length > 0 ||
                              xdomGroups.blogPosts.length > 0) && (
                              <optgroup label={XDOM_GROUP_LABELS.blog}>
                                {xdomGroups.blogHome.map((opt) => (
                                  <option key={opt.url} value={opt.url}>
                                    {xdomLabel(opt.path)}
                                  </option>
                                ))}
                                {xdomGroups.blogPosts.map((opt) => (
                                  <option
                                    key={opt.url}
                                    value={opt.url}
                                    style={{ textIndent: "1.5em" }}
                                  >
                                    {xdomLabel(opt.path)}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {xdomGroups.other.length > 0 && (
                              <optgroup label={XDOM_GROUP_LABELS.other}>
                                {xdomGroups.other.map((opt) => (
                                  <option key={opt.url} value={opt.url}>
                                    {xdomLabel(opt.path)}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {xdomGroups.sitePages.length > 0 && (
                              <optgroup label={XDOM_GROUP_LABELS.sitePages}>
                                {xdomGroups.sitePages.map((opt) => (
                                  <option key={opt.url} value={opt.url}>
                                    {xdomLabel(opt.path)}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {xdomGroups.unknown.length > 0 && (
                              <optgroup label={XDOM_GROUP_LABELS.unknown}>
                                {xdomGroups.unknown.map((opt) => (
                                  <option key={opt.url} value={opt.url}>
                                    {xdomLabel(opt.path)}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                              </>
                            )}
                          </div>
                        </td>
                        <td
                          className={`px-2 py-1.5 text-center align-middle ${
                            s.needsReview ? "bg-warning-bg" : "bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-end gap-2.5">
                            {s.needsReview && !s.skipped && (
                              <button
                                type="button"
                                onClick={() => void markReviewed(s)}
                                className="text-xs font-medium text-success hover:underline"
                                title="This target is correct — clear the warning"
                              >
                                OK
                              </button>
                            )}
                            {/* fix655: skip = intentionally no redirect (legal
                                pages with no equivalent). Skipped rows drop out
                                of the needs-target count and the export. */}
                            <button
                              type="button"
                              onClick={() => void toggleSkip(s, !s.skipped)}
                              className={`text-xs font-medium hover:underline ${
                                s.skipped ? "text-accent" : "text-neutral-400 hover:text-neutral-700"
                              }`}
                              title={
                                s.skipped
                                  ? "This page is skipped (no redirect). Click to un-skip."
                                  : "Skip — no redirect needed for this page"
                              }
                            >
                              {s.skipped ? "Skipped ↺" : "Skip"}
                            </button>
                            <button
                              type="button"
                              onClick={() => void removeRow(s)}
                              className="text-neutral-400 hover:text-danger text-sm"
                              title="Remove this row (excludes it from the redirect file)"
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Download */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* fix629: format is always "rewrite rules" (the web team uses
                    Plesk, whose default location blocks would collide with
                    location-style output). Shown as a static label so it's
                    clear which format the file is. */}
                <div className="flex items-center gap-1.5 text-xs text-text-muted">
                  <span>nginx format:</span>
                  <span className="px-2 py-1 rounded border border-border bg-bg-subtle text-text font-medium">
                    rewrite rules
                  </span>
                </div>
                <a
                  href={getOldDomainRedirectsUrl(client.id, "rewrite")}
                  className="bg-accent hover:bg-accent-dark text-white text-sm rounded-md px-4 py-2 no-underline"
                >
                  ↓ Download nginx redirects
                </a>
                <span className="text-[11px] text-text-subtle">
                  {matched} redirect{matched === 1 ? "" : "s"}
                  {unmatched > 0 && `; ${unmatched} listed as TODO for manual review`}
                </span>
              </div>

              {/* fix629: hint for the web team on where this file's contents go. */}
              <div className="text-[11px] text-text-muted bg-bg-subtle border border-border-subtle rounded-md px-3 py-2">
                Placed in: Apache &amp; nginx Settings → “Additional nginx
                directives”
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
